import { PlanWithFeatures } from '../types/Billing';
import { formatNumber } from '../utils/format';

export const en = {
  landing: {
    headline: 'Everything your business needs on WhatsApp',
    subheadline:
      'Marketing, notifications, chatbots, customer support, and deliveries — one dashboard.',
    signUp: 'Get Started Free',
    logIn: 'Log In',
    haveWhatsAppBusiness: 'Already have a WhatsApp Business account?',
    onboardViaFacebook: 'Connect via Facebook',
    viewPricing: 'View Pricing',
    watchDemos: 'Watch Demos',
    trustedBy: 'Trusted by',
    joinTheseCompanies:
      'Join these innovative companies already using our platform',
    seeItInAction: 'See It In Action',
    watchMoreDemos: 'Watch More Demos',
    products: {
      sectionTitle: 'One platform, four powerful apps',
      sectionSubtitle:
        'Each product solves a real problem. Activate only what you need.',
      clientell: {
        tagline: 'Bulk WhatsApp messaging',
        description:
          'Send personalized messages to thousands of customers at once. Create templates, organize contacts into groups, schedule campaigns, and track performance with real-time analytics.',
        features: [
          'Reusable message templates with dynamic placeholders',
          'Contact groups for targeted campaigns',
          'Schedule messages for the perfect time',
          'Delivery rates, read receipts, and analytics',
        ],
      },
      imminote: {
        tagline: 'Automated notifications via API',
        description:
          'Trigger WhatsApp notifications from your existing systems. Order confirmations, appointment reminders, status updates — all automated through a simple API.',
        features: [
          'REST API and multi-language SDKs',
          'Event-driven triggers and webhooks',
          'Template-based notifications',
          'Full delivery tracking and logs',
        ],
      },
      botmaker: {
        tagline: 'No-code chatbot builder',
        description:
          'Build conversational WhatsApp bots with a visual drag-and-drop editor. No coding required. Add AI-powered responses and connect to your business logic.',
        features: [
          'Visual drag-and-drop flow editor',
          'AI-powered responses (LLM integration)',
          'Conditional logic and branching',
          'Connect to external APIs and databases',
        ],
      },
      whatchat: {
        tagline: 'Shared inbox for live chat',
        description:
          'Manage all WhatsApp conversations in one shared inbox. Assign agents, track response times, and never miss a customer message.',
        features: [
          'Shared inbox for your entire team',
          'Agent assignment and routing',
          'Real-time conversation updates',
          'Full message history and search',
        ],
      },
      rakine: {
        tagline: 'Delivery management',
        description:
          'Track drivers, manage deliveries, and dispatch orders in real-time. Built for logistics teams that need visibility and control.',
        features: [
          'Real-time driver location tracking',
          'One-click delivery dispatch',
          'Delivery status and history',
          'Driver performance analytics',
        ],
      },
    },
    howItWorks: {
      title: 'Up and running in minutes',
      step1Title: 'Connect your number',
      step1Desc:
        'Link your WhatsApp Business number via Meta signup — takes under 5 minutes.',
      step2Title: 'Choose your tools',
      step2Desc:
        'Activate Clientell, Imminote, Botmaker, WhatChat, or Rakine — only pay for what you use.',
      step3Title: 'Launch',
      step3Desc:
        'Start messaging customers, automating notifications, or managing deliveries right away.',
    },
  },
  header: {
    dabsi: 'DABSI',
    clientell: 'Clientell',
    clientellDescription: 'Reach your customers',
    imminote: 'Imminote',
    imminoteDescription: 'Instant notifications',
    botmaker: 'Botmaker',
    botmakerDescription: 'Design your chatbot',
    rakine: 'Rakine',
    rakineDescription: 'Manage your deliveries',
    whatchat: 'WhatChat',
    whatchatDescription: 'Live conversations',
    home: 'Home',
    profile: 'Profile',
    loading: 'Loading...',
    myProfile: 'My Profile',
    manageUsers: 'Manage Users',
    managePlan: 'Manage Plan',
    apiDocs: 'API Documentation',
    accountId: 'Account ID:',
    tutorials: 'Tutorials',
  },
  footer: {
    faq: 'FAQ',
    demos: 'Demos',
    privacy: 'Privacy Policy',
    terms: 'Terms of Service',
    copyright: 'DABSI',
  },
  nav: {
    clients: 'Clients',
    groups: 'Groups',
    templates: 'Templates',
    users: 'Users',
    profile: 'Profile',
    api: 'API',
    sdk: 'SDK',
    settings: 'Settings',
    inbox: 'Inbox',
  },
  profile: {
    subscriptionDetails: 'Subscription Details',
    cancel: 'Cancel',
    cancelSubscription: 'Cancel Subscription',
    sureToCancel:
      "Are you sure you want to cancel your subscription? You'll lose access to:",
    sureToUndoCancel:
      'Are you sure you want to undo the cancellation of your subscription?',
    sureToDowngrade:
      "Are you sure you want to downgrade your subscription? You'll lose access to certain features.",
    allPremiumFeatures: 'All premium features',
    storedClientData: 'Stored client data beyond the free tier limit',
    advancedReporting: 'Advanced reporting capabilities',
    seePlans: 'See Plans',
    thisIsYourPlan: 'This is your current plan.',
    thisIsYourScheduledPlan: 'This is your scheduled plan.',
    upgradeNow: 'Upgrade now',
    scheduleUpgrade: 'Upgrade at next billing period',
    paymentInProgress: 'Payment in progress...',
    keepSubscription: 'Keep Subscription',
    confirmCancellation: 'Confirm Cancellation',
    cancelling: 'Cancelling...',
    undoSubscriptionCancellation: 'Undo Subscription Cancellation',
    keepCancellation: 'Keep Cancellation',
    restore: 'Restore',
    undoingCancel: 'Undoing Cancellation...',
    downgradeSubscription: 'Downgrade Subscription',
    keepCurrentPlan: 'Keep Current Plan',
    confirmDowngrade: 'Confirm Downgrade',
    downgrading: 'Downgrading...',
    currentPlan: 'Current Plan',
    upcomingPlan: 'Upcoming Plan',
    downgrade: 'Downgrade',
    status: 'Status',
    actions: 'Actions',
    nextBillingDate: 'Next Billing Date',
    cancellationDate: 'Cancellation Date',
    monthlyPrice: 'Monthly Price',
    recommended: 'Recommended',
    month: 'month',
    select: 'Select',
    simplePricing: 'Simple, Transparent Pricing',
    choosePlan: 'Choose the perfect plan for your business.',
    currency: 'Currency',
    customPlan: 'Need a custom plan? Contact our sales team',
    needCustomPlan: 'Need a custom plan?',
    contactSales: 'Contact Sales',
    tailoredSolution: 'Contact our sales team for a tailored solution',
    basic: 'Basic',
    active: 'active',
    trialing: 'trial',
    canceled: 'canceled',
    processing: 'Processing...',
    successPaymentTitle: 'Payment Successful 🙂',
    successPaymentContent:
      'Your payment was successful. Thank you for your purchase! Please refresh the page if the subscription status is not yet updated.',
    errorPaymentTitle: 'Uh Oh! Payment Error 🙁',
    errorPaymentContent:
      'There was an error processing your payment. Please try again.',
    close: 'Close',
    subscriptionTab: 'Subscription',
    dataTab: 'Data',
    role: 'Role',
    seeMoreDetails: 'See More Details',
    compareFeatures: 'Compare Features',
    frequentlyAskedQuestions: 'Frequently Asked Questions',
  },
  plans: {
    plan: 'Plan',
    price: 'Price',
    phoneNumbers: 'Phone Numbers',
    activationFee: 'One-time activation fee',
    monthlyMaintenanceFee: 'Monthly maintenance fee',
    utilization: 'utilization',
    free: 'Free',
    teamsAndClients: 'Team members & Clients',
    teamMembers: 'Team members',
    clients: 'Clients',
    groups: 'Groups',
    triggerRecipients: 'Notification recipients',
    templatesAndMessages: 'Templates & Messages',
    templates: 'Templates',
    utilityTemplateMessages: 'Utility template messages',
    marketingTemplateMessages: 'Marketing template messages',
    nonTemplateMessages: 'Non-template messages',
    features: 'Features',
    advancedAnalytics: 'Advanced analytics',
    conversationAccess: 'Access to client conversations',
    exportData: 'Export data',
    customReports: 'Custom reports',
    fullApiAccess: 'Full API access',
    apiRateLimit: 'API rate limit',
    msgsPerSecond: 'msgs/sec',
    customIntegrations: 'Custom integrations',
    accountManager: 'Dedicated account manager',
    multiLanguageSdkAccess: 'Multi-language SDK access',
    llm: 'LLM',
    llmDisclaimer:
      'To activate your AI chatbot, please add your API keys. We seamlessly integrate with leading language model providers including OpenAI, Anthropic, and more. Your keys remain secure and encrypted in our system.',
    dragAndDropBotMaker: 'Drag-and-drop bot maker',
    messageScheduling: 'Message scheduling',
    poc: 'Proof of Concept',
    pay_as_you_go: 'Pay as you go',
    enterprise: 'Enterprise',
    professional: 'Professional',
    basic: 'Basic',
    priority: 'Priority',
    support: 'Support',
    upTo: 'Up to',
    unlimited: 'Unlimited',
    pocOffer:
      'We also offer a free plan for testing purposes. Please contact us for more information.',
    messageRates: {
      title: '* Message Rates',
      description: 'Rates vary by destination country',
      country: 'Country',
      marketing: 'Marketing',
      utility: 'Utility/Auth',
      nonTemplate: 'Non-template',
      perMessage: '/msg',
      countries: {
        SN: 'Senegal',
        FR: 'France',
        US: 'USA',
        AE: 'UAE',
      },
    },
    highlightedPlanFeatures: {
      poc: (plan: PlanWithFeatures) => [
        `1 phone number`,
        `1 team member`,
        `Up to ${formatNumber('fr', plan.features.maxClients)} clients`,
        `Up to ${formatNumber('fr', plan.features.maxGroups)} groups`,
        `Up to ${formatNumber('fr', plan.features.maxTriggerRecipients)} notification recipients`,
        `Up to ${formatNumber('fr', plan.features.maxTemplates)} templates`,
        `Up to ${formatNumber('fr', plan.features.maxUtilityMessages)} utility template messages`,
        `Up to ${formatNumber('fr', plan.features.maxMarketingMessages)} marketing template messages`,
        `Unlimited non-template messages`,
        `Advanced analytics`,
        `Export data`,
        `Multi-language SDK access`,
        `Basic support`,
        `No activation fee`,
        `No monthly maintenance fee`,
      ],
      pay_as_you_go: (plan: PlanWithFeatures) => [
        `1 phone number`,
        `2 team members`,
        `Up to ${formatNumber('fr', plan.features.maxClients)} clients`,
        `Up to ${formatNumber('fr', plan.features.maxGroups)} groups`,
        `Up to ${formatNumber('fr', plan.features.maxTriggerRecipients)} notification recipients`,
        `Up to ${formatNumber('fr', plan.features.maxTemplates)} templates`,
        `Up to ${formatNumber('fr', plan.features.maxUtilityMessages)} utility template messages`,
        `Up to ${formatNumber('fr', plan.features.maxMarketingMessages)} marketing template messages`,
        `Unlimited non-template messages`,
        `Advanced analytics`,
        `Export data`,
        `Full API access (100 msgs/sec)`,
        `Multi-language SDK access`,
        `LLM`,
        `Drag-and-drop bot maker`,
        `Basic support`,
      ],
      basic: (plan: PlanWithFeatures) => [
        `1 phone number`,
        `1 team member`,
        `Up to ${formatNumber('fr', plan.features.maxClients)} clients`,
        `Up to ${formatNumber('fr', plan.features.maxGroups)} groups`,
        `Up to ${formatNumber('fr', plan.features.maxTriggerRecipients)} notification recipients`,
        `Up to ${formatNumber('fr', plan.features.maxTemplates)} templates`,
        `Up to ${formatNumber('fr', plan.features.maxUtilityMessages)} utility template messages`,
        `Up to ${formatNumber('fr', plan.features.maxMarketingMessages)} marketing template messages`,
        `Unlimited non-template messages`,
        `Advanced analytics`,
        `Export data`,
        `Multi-language SDK access`,
        `LLM`,
        `Drag-and-drop bot maker`,
        `Basic support`,
        `No monthly maintenance fee`,
      ],
      professional: (plan: PlanWithFeatures) => [
        `Up to ${formatNumber('fr', plan.features.maxPhoneNumbers)} phone numbers`,
        `Up to ${formatNumber('fr', plan.features.maxUsers)} team members`,
        `Up to ${formatNumber('fr', plan.features.maxClients)} clients`,
        `Up to ${formatNumber('fr', plan.features.maxGroups)} groups`,
        `Up to ${formatNumber('fr', plan.features.maxTriggerRecipients)} notification recipients`,
        `Up to ${formatNumber('fr', plan.features.maxTemplates)} templates`,
        `Up to ${formatNumber('fr', plan.features.maxUtilityMessages)} utility template messages`,
        `Up to ${formatNumber('fr', plan.features.maxMarketingMessages)} marketing template messages`,
        `Unlimited non-template messages`,
        `Advanced analytics`,
        `Export data`,
        `Custom reports`,
        `Full API access (100 msgs/sec)`,
        `Multi-language SDK access`,
        `LLM`,
        `Drag-and-drop bot maker`,
        `Priority email and chat support`,
        `No monthly maintenance fee`,
      ],
      enterprise: (plan: PlanWithFeatures) => [
        `Up to ${formatNumber('fr', plan.features.maxPhoneNumbers)} phone numbers`,
        `Up to ${formatNumber('fr', plan.features.maxUsers)} team members`,
        `Up to ${formatNumber('fr', plan.features.maxClients)} clients`,
        `Up to ${formatNumber('fr', plan.features.maxGroups)} groups`,
        `Up to ${formatNumber('fr', plan.features.maxTriggerRecipients)} notification recipients`,
        `Up to ${formatNumber('fr', plan.features.maxTemplates)} templates`,
        `Up to ${formatNumber('fr', plan.features.maxUtilityMessages)} utility template messages`,
        `Up to ${formatNumber('fr', plan.features.maxMarketingMessages)} marketing template messages`,
        `Unlimited non-template messages`,
        `Message scheduling`,
        `Advanced analytics`,
        `Access to client conversations`,
        `Export data`,
        `Custom reports`,
        `Full API access (100 msgs/sec)`,
        `Custom integrations`,
        `Multi-language SDK access`,
        `LLM`,
        `Drag-and-drop bot maker`,
        `Priority email and chat support`,
        `Dedicated account manager`,
        `No monthly maintenance fee`,
      ],
    },
  },
  billing: {
    startFullSubscription: 'Start Full Subscription',
    updateSubscription: 'Update Subscription',
    changePlan: 'Change Plan',
    reactivateSubscription: 'Reactivate Subscription',
    trialEndsSoon: 'Your trial ends soon on',
    trialUntil: 'You are currently in trial mode until',
    shouldRenewSoon: 'Please renew your subscription soon by',
    gracePeriodPayment:
      'Your payment is late. Please renew your subscription or it will expire on',
    subscriptionCancelling: 'Your subscription is being cancelled on',
    subscriptionExpired: 'Your subscription has expired.',
    subscriptionCanceled: 'Your subscription has been canceled.',
    undoCancellation: 'Undo cancellation',
    subscriptionCancelled: 'Subscription has been cancelled.',
    subscriptionCancellationError:
      'Failed to cancel subscription. Please try again later.',
    subscriptionRestored: 'Subscription has been restored!',
    subscriptionRestoreError:
      'Failed to undo subscription cancellation. Please try again later.',
    currentSubscriptionKept: 'Kept current subscriptiont!',
    currentSubscriptionKeepError:
      'Failed to keep current subscription. Please try again later.',
    subscriptionUpgradeScheduled: 'Subscription upgrade has been scheduled.',
    subscriptionUpgradeScheduleError:
      'Failed to schedule subscription upgrade. Please try again later.',
    subscriptionDowngradeScheduled:
      'Subscription downgrade has been scheduled.',
    subscriptionDowngradeScheduleError:
      'Failed to schedule subscription downgrade. Please try again later.',
    choosePaymentMethod: 'Choose payment method',
    payWithWave: 'Pay with Wave',
    payWithBictorys: 'Pay with Bictorys',
    paymentSuccess:
      'Payment successful! Please refresh the page to see the changes.',
    paymentError: 'Payment failed. Please try again.',
    checkoutInitializationError:
      'Unable to start checkout right now. Please check your connection and try again.',
    subscriptionStatusFetchError:
      'We cannot fetch the status of your subscription payment at this time. Please refresh the page.',
    monthly: 'Monthly',
    yearly: 'Yearly',
    minusTwentyPercent: '-20%',
    faqs: [
      {
        question: 'How does the trial work?',
        answer:
          "There is a 14-day free trial. You'll get full access to all features in the basic plan until the trial period expires. No credit card or commitment required.",
      },
      {
        question: 'Can I change plans later?',
        answer:
          'Yes, you can upgrade or downgrade your plan at any time. Your new plan and its features will be active starting your next billing period. Your current plan remains active until then.',
      },
      {
        question: 'What payment methods do you accept?',
        answer:
          'We accept card payments, Orange Money, and Wave Money. Enterprise customers can explore additional payment options.',
      },
      {
        question: 'What happens when I exceed my plan limits?',
        answer:
          "We'll notify you when you're approaching your limits. You can upgrade to a higher plan or contact us for a custom solution.",
      },
      {
        question: 'Do you offer refunds?',
        answer:
          "You can cancel your subscription at any time from your profile settings. If you have concerns or issues, contact our support team - we're happy to discuss your situation and explore options on a case-by-case basis.",
      },
      {
        question: 'Do you offer discounts for non-profits?',
        answer:
          'Yes, we offer special pricing for registered non-profit organizations. Contact our sales team for details.',
      },
      {
        question:
          'What happens to my clients when I downgrade my subscription?',
        answer: `When you downgrade your subscription, your client limit will be reduced according to your new plan. For example, if you downgrade from the Professional plan to the Basic plan, you'll still have access to all your data, but with some limitations:

                - Excess clients: Clients beyond your new limit (determined by least recently active) will be placed in read-only mode.
                - Limited functionality: For these excess clients, you won't be able to:
                • Send message templates
                • Add them to groups
                • Make changes to their information

                You'll still be able to view all client data and history.`,
      },
      {
        question: 'What happens to my groups after downgrading?',
        answer: `Similar to clients, groups exceeding your new plan's limits will be set to read-only mode (determined by least recently updated). For these excess groups:

                - You cannot add new clients to them
                - You cannot send message templates to them
                - You can still view all group data and message history`,
      },
      {
        question: 'What happens to my templates after downgrading?',
        answer: `Templates beyond your new plan's limit will be in read-only mode:

                - You can view these templates
                - You cannot edit them
                - You cannot use them to send messages`,
      },
      {
        question: 'What happens to my user accounts after downgrading?',
        answer:
          "User accounts beyond your new plan's limit will be set to read-only. These users will not be able to make changes but can still view data according to their existing permissions.",
      },
      {
        question:
          'How do I choose which clients/groups/templates remain active?',
        answer:
          'The system automatically sets your least recently active items to read-only. In the future, you will be able manually adjust which items remain active.',
      },
      {
        question: 'Can I get back my full access?',
        answer:
          'Yes! You can upgrade your subscription at any time to restore full functionality to all your data. No information is deleted during the downgrade process.',
      },
      {
        question: 'What are the API rate limits?',
        answer:
          'Plans with full API access (Pay as you go, Professional, and Enterprise) include a rate limit of 100 messages per second. This applies to the Send API for sending WhatsApp messages programmatically. If you need higher throughput, contact our sales team for a custom solution.',
      },
    ],
  },
  home: {
    viewOrgs: 'View Organizations',
    addOrg: 'Add Organization',
    manageOrg: 'Manage Organization',
    yourOrgs: 'Your Organizations',
    addPhoneNumbersToOrg: 'Add Phone Numbers to Organizations',
    orgName: 'Organization Name',
    orgAddedSuccessfully: 'Organization added successfully!',
    errorAddingOrg: 'Error adding organization...',
    noOrgs: 'You are not in any organization at this moment.',
    add: 'Add',
  },
  modules: {
    clients: 'Clients',
    clientsDesc: 'Manage your contacts',
    groups: 'Groups',
    groupsDesc: 'Organize contacts into groups',
    templates: 'Templates',
    templatesDesc: 'Create and send messages',
    users: 'Users',
    profile: 'Profile',
    api: 'API',
    sdk: 'SDK',
    settings: 'Settings',
  },
  clients: {
    clients: 'Clients',
    viewClients: 'View Clients',
    editMode: 'Edit mode',
    search: 'Search clients...',
    searchResultsEmpty: 'No clients found...',
    selectCountry: 'Select country',
    country: 'Country',
    phoneNumber: 'Phone Number',
    email: 'Email',
    emailOptional: 'Email (optional)',
    firstNameOptional: 'First Name (optional)',
    middleNameOptional: 'Middle Name (optional)',
    lastNameOptional: 'Last Name (optional)',
    addClients: 'Add Clients',
    bulkAddClientsToOrg: 'Bulk Add Clients to Organization',
    addClientToOrg: 'Add Client to Organization',
    uploadCsv: 'Upload CSV',
    addClient: 'Add Client',
    bulkAddedSuccessfully: 'Clients added in bulk sucessfully!',
    errorAddingInBulk: 'Error adding clients in bulk...',
    addedToGroupSuccessfully: 'Client added to group sucessfully!',
    errorAddingToGroup: 'Error adding client to group...',
    bulkAddedToGroupSuccessfully: 'Clients added to group in bulk sucessfully!',
    errorAddingToGroupInBulk: 'Error adding clients to group in bulk...',
    noClientsYet: 'This organization has not added any clients yet.',
    uploading: 'Uploading...',
    addClientSuccess: 'Client added to organization sucessfully!',
    addClientError: 'Error adding client to organization...',
    successfullyAdded: 'Successfully added',
    clientsToOrg: 'clients to organization.',
    csvUploadError: 'Failed to upload CSV. Please try again.',
    successfullyUpdatedName: 'Name successfully updated for',
    errorUpdatingName: 'Error updating name for',
    successfullyUpdatedPhoneNumber: 'Phone number successfully updated for',
    errorUpdatingPhoneNumber: 'Error updating phone number for',
    successfullyDeletedClient: 'Successfully deleted client',
    errorDeletingClient: 'Error deleting client',
    phoneNumberRequired: 'Phone number is required',
    invalidEmail: 'Please enter a valid email address',
    selectCsvToAdd: 'Upload CSV to add clients',
    csvHeaders: 'The CSV should have a header row with the following columns:',
    csvRequiredColumns:
      'Only country and phone number are required. The rest are optional.',
    viewGroups: 'View Groups',
    totalGroups: 'Total Groups',
    errorFetchingGroups: 'Error fetching groups for client...',
    notInAnyGroup: 'This client is not in any group yet',
  },
  optIn: {
    thanks: 'Thanks for opting in to messages from',
    invalidLink: 'This link is invalid',
    loading: 'Loading...',
    enterPhone: 'Enter your phone number to opt in to messages from',
    phonePlaceholder: '7X XXX XX XX',
    sendCode: 'Send verification code',
    enterCode: 'Enter verification code',
    codeSent: 'A code was sent via WhatsApp to',
    verify: 'Verify',
    wrongCode: 'Invalid code. Please try again.',
    sendFailed: 'Failed to send code. Please try again.',
  },
  groups: {
    groups: 'Groups',
    viewGroups: 'View Groups',
    editMode: 'Edit mode',
    search: 'Search groups...',
    searchResultsEmpty: 'No groups found...',
    addGroupToOrg: 'Add Group to Organization',
    addClientToGroup: 'Add Client to Group',
    noGroups: 'No groups available to add client to.',
    noClients: 'No clients available to add to group.',
    groupName: 'Group Name',
    descriptionOptional: 'Description (optional)',
    selectGroup: 'Select group',
    selectClient: 'Select client',
    addGroup: 'Add Group',
    addClients: 'Add Clients',
    uploading: 'Uploading...',
    successfullyAdded: 'Successfully added',
    clientsToGroups: 'clients to groups',
    csvUploadFailed: 'Failed to upload CSV. Please try again.',
    or: 'or',
    selectCsvToAddInBulk: 'Upload CSV to add in bulk',
    csvHeaders: 'The CSV should have a header row with the following columns:',
    csvRequiredColumns:
      'Only country and phone number are required. The rest are optional.',
    groupCreatedSuccessfully: 'Group created successfully!',
    errorCreatingGroup: 'Error creating group...',
    groupNameSuccessfullyUpdated: 'Group name successfully updated to',
    errorUpdatingGroupName: 'Error updating group name',
    descriptionSuccessfullyUpdated: 'Description successfully updated for',
    errorUpdatingDescription: 'Error updating description for',
    clientsLowercase: 'clients',
    emptyGroup: 'This group is empty...',
    noGroupsYet: 'This organization has not added any groups yet.',
    successfullyDeletedGroup: 'Successfully deleted group',
    errorDeletingGroup: 'Error deleting group',
    viewClients: 'View Clients',
    totalClients: 'Total Clients',
    addedAt: 'Added',
    addedBy: 'Added by',
    errorFetchingClients: 'Error fetching clients for group...',
    searchGroup: 'Search groups...',
    removeFromGroup: 'Remove from group',
    remove: 'Remove',
    confirmRemoveClient:
      'Are you sure you want to remove this client from the group?',
    clientRemoved: 'Client removed from group',
    errorRemovingClient: 'Failed to remove client',
  },
  templates: {
    viewAndSendTemplates: 'Send Messages',
    allTemplates: 'All Templates',
    refresh: 'Refresh',
    showApprovedOnly: 'Show approved templates only',
    noTemplatesFound: 'No templates found',
    templateDetails: 'Template Details',
    selectTemplate: 'Select a template to view details or send message',
    selectTemplateToPreview: 'Select a template to see a preview',
    template: 'Template',
    notApproved: 'This template is currently not approved.',
    pendingApproval: 'This template is currently pending approval...',
    templatePaused:
      'This template has been paused by Meta due to low quality or high negative feedback.',
    templateDisabled: 'This template has been disabled by Meta.',
    templateLimitExceeded: 'This template has exceeded its messaging limit.',
    statusApproved: 'Approved',
    statusPending: 'Pending',
    statusRejected: 'Rejected',
    statusPaused: 'Paused',
    statusDisabled: 'Disabled',
    statusLimitExceeded: 'Limit exceeded',
    statusInAppeal: 'In appeal',
    statusDeleting: 'Deleting',
    statusDeleted: 'Deleted',
    selectDifferentImage: 'Select different image',
    selectDifferentVideo: 'Select different video',
    editHeaderParameters: 'Edit header parameters',
    editBodyParameters: 'Edit body parameters',
    messagePreview: 'Message Preview',
    templatePreview: 'Template Preview',
    previewNotExact:
      "The final appearance of the message may differ from the preview depending on the recipient's device.",
    buttons: 'Buttons',
    sendTemplate: 'Send Template',
    sendMessage: 'Send Message',
    backToTemplates: 'Templates',
    close: 'Close',
    sendingFrom: 'Sending from:',
    selectPhoneNumber: 'Select a phone number to send from',
    selectRecipients: 'Select recipients to send to',
    sendTo: 'Send template to:',
    organization: 'Organization',
    groups: 'Groups',
    clients: 'Clients',
    phones: 'Phone Numbers',
    selectGroup: 'Select group',
    selectClient: 'Select client',
    enterPhoneNumber: 'Enter phone number',
    add: 'Add',
    theTemplateMessage: 'The template message',
    templateWillBeSentToOrg:
      'will be sent to all the recipients within the organization',
    templateWillBeSentToGroup:
      'will be sent to all the recipients within the group',
    send: 'Send',
    sendNow: 'Send Now',
    schedule: 'Schedule',
    scheduleMessage: 'Schedule Message',
    scheduledFor: 'Scheduled for',
    selectDateTime: 'Select date and time',
    scheduleFor: 'Schedule for:',
    sendOrSchedule: 'Send or schedule message',
    scheduledSuccessfully: 'Message scheduled successfully!',
    errorScheduling: 'Error scheduling message...',
    dateTimeMustBeFuture: 'Scheduled time must be in the future',
    cancelScheduledMessage: 'Cancel Scheduled Message',
    cancelSchedule: 'Cancel Schedule',
    cancelScheduleConfirmation:
      'Are you sure you want to cancel this scheduled message? This action cannot be undone.',
    viewScheduledMessages: 'View scheduled messages',
    scheduledMessages: 'Scheduled Messages',
    noScheduledMessages: 'No scheduled messages',
    scheduled: 'Scheduled',
    canceled: 'Canceled',
    scheduleCanceled: 'Schedule canceled successfully',
    errorCanceling: 'Error canceling schedule...',
    errorFetchingScheduledMessages: 'Error fetching scheduled messages...',
    scheduledDescription:
      'Messages waiting to be sent at their scheduled time.',
    scheduledSentDescription: 'Scheduled messages that were successfully sent.',
    scheduledCanceledDescription:
      'Scheduled messages that were canceled before sending.',
    scheduledFailedDescription:
      'Scheduled messages that failed to send due to an error.',
    allScheduledMessages: 'All Scheduled Messages',
    scheduledTime: 'Scheduled Time',
    audience: 'Audience',
    repeatMessage: 'Repeat',
    repeatsColumn: 'Repeats',
    pause: 'Pause',
    resume: 'Resume',
    scheduledStatusPaused: 'Paused',
    scheduledStatusCompleted: 'Completed',
    recurrenceFrequency: 'Frequency',
    recurrenceDaily: 'Daily',
    recurrenceWeekly: 'Weekly',
    recurrenceMonthly: 'Monthly',
    recurrenceDayOfMonth: 'Day of month',
    recurrenceDayOfMonthHint: 'Capped at 28 to avoid Feb/30/31 ambiguity.',
    recurrenceTimeOfDay: 'Time of day',
    recurrenceTimeOfDayHint: 'HH:mm (24-hour)',
    recurrenceTimezone: 'Timezone',
    recurrenceEnds: 'Ends',
    recurrenceEndsNever: 'Never',
    recurrenceEndsOn: 'On date',
    recurrenceEndsAfter: 'After N occurrences',
    recurrenceEndDate: 'End date',
    recurrenceEndCount: 'Number of occurrences',
    recurrencePreview: 'Preview',
    recurrenceOccurrences: 'Occurrences',
    recurrenceEveryDay: 'Every day',
    recurrenceEvery: 'Every',
    recurrenceAt: 'at',
    recurrenceMonthlyOnDay: 'Monthly on day',
    weekdaySun: 'Sun',
    weekdayMon: 'Mon',
    weekdayTue: 'Tue',
    weekdayWed: 'Wed',
    weekdayThu: 'Thu',
    weekdayFri: 'Fri',
    weekdaySat: 'Sat',
    viewOccurrenceHistory: 'View history',
    invalidRecurrenceError:
      'Recurrence settings are incomplete or invalid. Check the time of day, weekday selection, or day of month.',
    occurrenceHistoryTitle: 'Occurrence history',
    occurrenceHistoryEmpty: 'No occurrences yet.',
    occurrenceWhen: 'When',
    occurrenceError: 'Error',
    loadMore: 'Load more',
    pausedSuccessfully: 'Schedule paused',
    errorPausing: 'Error pausing schedule',
    resumedSuccessfully: 'Schedule resumed',
    errorResuming: 'Error resuming schedule',
    onTheWay: 'Your message is on the way...',
    viewSentMessages: 'View sent messages',
    errorSending: 'Error sending message...',
    createTemplate: 'Create Template',
    templateNameAndLanguage: 'Template name and language',
    nameYourTemplate: 'Name your template (lowercase and underscores only)',
    selectLanguage: 'Select language',
    french: 'French',
    english: 'English',
    category: 'Category',
    selectCategory: 'Select category',
    content: 'Content',
    contentSubtitle:
      'Fill out the header, body and footer sections of your template.',
    header: 'Header',
    enterHeaderText: 'Enter body text',
    selectHeaderType: 'Select header type',
    selectHeaderImage: 'Select header image',
    selectHeaderVideo: 'Select header video',
    changeImage:
      'You will be able to change the image when sending the template message.',
    changeVideo:
      'You will be able to change the video when sending the template message.',
    samplesForHeader: 'Samples for header content',
    samplesForBody: 'Samples for body content',
    exampleForEachHeaderVariable:
      'Please add an example for each variable in your header text. Do not use real customer information.',
    exampleForEachBodyVariable:
      'Please add an example for each variable in your body text. Do not use real customer information.',
    tooManyBodyVars:
      'This template body contains too many variable parameters. You need to decrease the number of variable parameters or increase the message length.',
    none: 'None',
    text: 'Text',
    image: 'Image',
    video: 'Video',
    body: 'Body',
    enterBodyText: 'Enter body text',
    footer: 'Footer',
    enterFooterText: 'Enter footer text',
    optional: 'Optional',
    buttonsSubtitle:
      'Create buttons that let customers respond to your message or take action. You can add up to 10 buttons.',
    addButton: 'Add',
    create: 'Create',
    creating: 'Template is being created...',
    createdSuccessfully: 'Template created successfully!',
    errorCreating: 'Error creating template...',
    pleaseEnterTemplateName: 'Please enter a template name',
    pleaseEnterBody: 'Please enter the message body',
    pleaseUploadMedia: 'Please upload an image or video for the header',
    pleaseFillAllVariables: 'Please fill in all variable examples',
    pleaseFillRequiredFields: 'Please fill in all required fields',
    language: 'Language',
    sentMessages: 'Sent Messages',
    byGroup: 'By Group',
    byClient: 'By Client',
    byTemplate: 'By Template',
    byOrg: 'By Organization',
    goBack: 'Go Back',
    confirm: 'Confirm',
    name: 'Name',
    total: 'Total',
    queued: 'Queued',
    processing: 'Processing',
    stuck: 'Stuck',
    outbound: 'Outbound',
    sent: 'Sent',
    delivered: 'Delivered',
    read: 'Read',
    failed: 'Failed',
    lastSent: 'Last sent',
    noMessagesYet: 'No messages sent yet...',
    searchClients: 'Search clients...',
    searchTemplates: 'Search templates...',
    noMessagesFound: 'No messages found...',
    templateName: 'Template Name',
    sentAt: 'Sent at',
    status: 'Status',
    lastUpdated: 'Last updated',
    errors: 'Errors',
    errorFetchingMessagesForClient: 'Error fetching messages for client...',
    messagesByStatus: 'Messages by Status',
    messageVolumeTrend: 'Daily Message Volume Trend',
    totalMessages: 'Total Messages',
    activeTemplates: 'Active Templates',
    deliveryRate: 'Delivery Rate',
    activeRecipients: 'Active Recipients',
    totalMessagesOrgDesc:
      'Total count of all template messages sent by this organization across all time periods.',
    activeTemplatesDesc:
      'Number of unique message templates that have been used in the last 30 days.',
    deliveryRateDesc:
      'Percentage of messages that were sent, delivered, or read (success rate) out of all completed messages (successful + failed/stuck).',
    period: 'Period',
    days7: '7 days',
    days30: '30 days',
    days90: '90 days',
    all: 'All',
    compare: 'Compare',
    activeClients: 'Active Clients',
    totalMessagesLabel: 'Total Messages',
    successRate: 'Success Rate',
    failures: 'Failures',
    activeClientsDesc:
      'Number of unique clients who have been sent at least one template message.',
    totalMessagesDesc:
      'Total number of template messages sent across all clients and templates.',
    successRateDesc:
      'Percentage of messages that were successfully sent, delivered, or read. This excludes messages that failed or got stuck.',
    failuresDesc:
      'Number of messages that failed to send or got stuck during processing.',
    currentPeriod: 'Current period',
    previousPeriod: 'Previous period',
    noDataAvailable: 'No data available for this period',
    failuresAndErrors: 'Failures / Errors',
    error: 'Error',
    errorCode: 'Error code',
    errorCategoryMeta: 'WhatsApp API Error',
    errorCategoryTechnical: 'Technical Error',
    errorCategoryUnknown: 'Unknown Error',
    moreErrors: 'more error(s)',
    numberOfMessages: 'Number of messages',
    deliveryFunnel: 'Message Delivery Funnel',
    readRate: 'Read Rate',
    overallReadRate: 'Overall Read Rate',
    messageCount: 'Messages',
    topPerformingTemplates: 'Top Performing Templates',
    successRatePercent: 'Success Rate (%)',
    comingSoon: 'Coming soon...',
    notifications: 'Notifications',
    confirmDeleteTitle: 'Confirm Deletion',
    deleteConfirmation: 'Are you sure you want to delete this template?',
    cancel: 'Cancel',
    delete: 'Delete',
    deletingTemplate: 'Template is being deleted...',
    templateSuccessfullyDeleted: 'Template successfully deleted!',
    errorDeletingTemplate: 'Error deleting template...',
    loading: 'Loading...',
    enterButtonText: 'Enter button text',
    quickReply: 'Quick Reply',
    phoneNumberButton: 'Phone Number',
    urlButton: 'URL Link',
    copyCode: 'Copy Code',
    buttonPhoneNumberPlaceholder: 'Phone number (e.g. +1234567890)',
    buttonUrlPlaceholder: 'Enter URL',
    buttonCodePlaceholder: 'Code to copy',
    categoryMarketing: 'Marketing',
    categoryUtility: 'Utility',
    categoryAuthentication: 'Authentication',
    couponCode: 'Coupon Code',
    enterCouponCode: 'Enter coupon code',
    buttonParameters: 'Button Parameters',
    messageValidityPeriod: 'Message validity period',
    validityPeriodDescription1:
      'You can set a custom validity period that your utility message must be delivered by before it expires. If a message is not delivered within this timeframe, you will not be charged and your customer will not see the message.',
    validityPeriodDescription2:
      "If you don't set a custom validity period, the standard 30 days WhatsApp message validity period will be applied.",
    setCustomValidityPeriod: 'Set custom validity period for your message',
    variables: 'Variables',
    variableTypeTitle: 'Type of variable',
    variableTypeDescription:
      'Variables are placeholders that are used to dynamically insert specific information or data into your template. You can insert either a name or number as a variable.',
    examples: 'Examples:',
    variableNameExample: 'Name: {{order_id}}',
    variableNumberExample: 'Number: {{1}}',
    variableTypeNumber: 'Number',
    variableTypeNamed: 'Named',
    minuteSingular: 'minute',
    minutesPlural: 'minutes',
    // Conversation drawer
    conversation: 'Conversation',
    conversationTemplates: 'Templates',
    conversationClientReplies: 'Client Replies',
    conversationBotResponses: 'Bot Responses',
    conversationAgentMessages: 'Agent',
    messageTypeTemplate: 'Template',
    messageTypeClient: 'Client',
    messageTypeBot: 'Bot',
    messageTypeAgent: 'Agent',
    timeNow: 'now',
    noMessagesInConversation: 'No messages in this conversation',
    noMessagesMatchFilters: 'No messages match selected filters',
    sessionActive: 'Active',
    sessionActiveTooltip:
      'Contact messaged within 24h — free-form replies are possible',
    marketingDeliveryTip:
      'Marketing messages may have lower delivery rates. Contacts who have previously interacted with your business are more likely to receive them.',
    copyOptInLink: 'Copy opt-in link',
    optInLinkCopied: 'Copied!',
    previewTemplate: 'Preview',
    startTypingToPreview: 'Start typing to see preview',
    viewHistory: 'View Conversation History',
    conversationHistory: 'Conversation History',
    // Field mapping for personalized parameters
    switchToStatic: 'Use same value for all',
    switchToField: 'Personalize per recipient',
    selectClientField: 'Select client field',
    fieldMayBeEmpty: 'May be empty for some recipients',
    emptyFieldValue: '(empty)',
    phoneDisabledWithFieldMapping:
      'Phone numbers cannot be used with personalized fields',
    fieldFirstName: 'First Name',
    fieldLastName: 'Last Name',
    fieldMiddleName: 'Middle Name',
    fieldPhoneNumber: 'Phone Number',
    fieldEmail: 'Email',
    previewRecipients: 'Sample Preview',
    noClientsFound: 'No clients found for preview',
  },
  api: {
    apiDocumentation: 'API Documentation',
    apiDocumentationDescription:
      'Explore various API usage examples in different programming languages to quickly integrate the API into your application.',
    apiSettings: 'API Settings',
    apiSettingsDescription:
      'Manage your API keys and settings for seamless integration.',
    apiCredentials: 'API Credentials',
    apiKey: 'API Key',
    apiKeyDescription:
      'Your API key is used to authenticate requests to the API. Keep it secret and secure.',
    copiedApiKey: 'API Key copied to clipboard',
    copyApiKeyError: 'Could not copy API Key',
    generateNewApiKey: 'Generate New API Key',
    oldKeyWillBeInvalidated:
      'The old key will be invalidated if you generate a new one.',
    apiInformation: 'API Information',
    currentVersion: 'Current Version',
    currentVersionDescription: 'Latest version with improved performance',
    viewApiDocumentation: 'View API Documentation',
    viewDocumentationDescription: 'Find integration guides and code samples',
    sdkInformation: 'SDK Information',
    viewSdkDocumentation: 'View SDK Documentation',
    requestHeaders: 'Request Headers',
    requestBody: 'Request Body',
    exampleFunction: 'Example integration',
    exampleUsage: 'Example usage',
    in: 'in',
    sendNotification: 'Send Notification',
    sendNotificationDescription:
      'Send a pre-approved template message to a list of recipients',
    loadError: 'Failed to load API documentation',
    loadErrorDescription:
      'The API documentation could not be loaded. Please check your connection and try again.',
  },
  subscriptionBanner: {
    trialActive: 'Your trial ends on {date}.',
    trialEndingSoon: 'Your trial ends in {days} {dayUnit} ({date}).',
    renewalComing: 'Your subscription renews on {date}.',
    appsCancelling: '{apps} will be cancelled at renewal ({date}).',
    gracePeriod: 'Payment overdue. Pay by {date} to keep your apps active.',
    expired: 'Your subscription has expired. Re-subscribe to regain access.',
    payNow: 'Pay now',
    managePlan: 'Manage Plan',
    restore: 'Restore',
    trialExpired: 'Your trial has ended. Subscribe to keep using your apps.',
    subscribe: 'Subscribe',
  },
  export: {
    title: 'Export Data',
    description:
      'Export your organization data in CSV or JSON format. Large exports will be processed in the background.',
    lockedMessage:
      'Subscribe to the Data Export add-on to export your organization data.',
    selectData: 'Select data to export',
    selectAll: 'Select All',
    clients: 'Clients',
    groups: 'Groups (including membership)',
    templates: 'Templates',
    messages: 'Messages (sent, received, bot)',
    imminoteApiUsage: 'Imminote API Usage',
    botFlows: 'Bot Flows',
    format: 'Export format',
    csv: 'CSV',
    json: 'JSON',
    exportButton: 'Export Data',
    exporting: 'Exporting...',
    exportQueued:
      'Your export is being processed. You will receive an email when it is ready.',
    exportReady: 'Export ready for download',
    downloadExport: 'Download',
    rateLimitWarning: 'You can only export once every 24 hours',
    nextAvailableAt: 'Next export available at',
    exportHistory: 'Export History',
    noExports: 'No exports yet',
    status: 'Status',
    requestedAt: 'Requested',
    completedAt: 'Completed',
    fileSize: 'Size',
    actions: 'Actions',
    pending: 'Pending',
    processing: 'Processing',
    completed: 'Completed',
    failed: 'Failed',
    asyncNote:
      'Exports containing messages are processed in the background. You will receive an email when your export is ready.',
    selectAtLeastOne: 'Please select at least one data type to export',
    exportSuccess: 'Export completed successfully',
    exportError: 'Export failed. Please try again.',
    upgradeRequired: 'Data export is available on higher plans',
    adminOnly: 'Only administrators can export data',
    stillProcessing:
      'Your export is still being processed. Please check back shortly.',
    jobNotFound: 'Export not found. It may have expired or been deleted.',
  },
  erasure: {
    title: 'Data Erasure',
    description:
      'Request permanent deletion of client or user data. Erasure requests have a 30-day grace period before execution.',
    entityType: 'Type',
    client: 'Client',
    user: 'User',
    selectClient: 'Select a client',
    selectUser: 'Select a user',
    searchPlaceholder: 'Search by name, phone, or email...',
    requestErasure: 'Request Erasure',
    forceErase: 'Force Erase Now',
    confirmRequestTitle: 'Confirm Erasure Request',
    confirmRequestMessage:
      'This will schedule permanent deletion of all personal data for this entity after the 30-day grace period. This action can be canceled during the grace period.',
    confirmForceTitle: 'Force Immediate Erasure',
    confirmForceMessage:
      'This will IMMEDIATELY and PERMANENTLY delete all personal data for this entity. This action cannot be undone.',
    confirmForceCheckbox: 'I understand this is irreversible',
    pending: 'Pending',
    executing: 'Executing',
    completed: 'Completed',
    canceled: 'Canceled',
    failed: 'Failed',
    requestHistory: 'Erasure Requests',
    status: 'Status',
    type: 'Type',
    entity: 'Entity',
    requestedBy: 'Requested By',
    requestedAt: 'Requested',
    gracePeriodEndsAt: 'Grace Period Ends',
    executedAt: 'Executed',
    actions: 'Actions',
    cancel: 'Cancel',
    cancelSuccess: 'Erasure request canceled',
    requestSuccess:
      'Erasure request created. Data will be permanently deleted after the 30-day grace period.',
    forceSuccess: 'Data has been permanently erased',
    alreadyPending: 'An erasure request is already pending for this entity',
    notFound: 'Entity not found',
    superadminRequired: 'Superadmin access required for immediate erasure',
    noRequests: 'No erasure requests yet',
    daysRemaining: 'days remaining',
  },
  rakine: {
    modules: {
      overview: 'Overview',
      overviewDesc: 'Active drivers, deliveries, and revenue summary',
      drivers: 'Drivers',
      driversDesc: 'Manage delivery drivers',
      deliveries: 'Deliveries',
      deliveriesDesc: 'Track and manage deliveries',
      dispatch: 'Dispatch',
      dispatchDesc: 'Create and assign deliveries',
      settings: 'Settings',
      settingsDesc: 'Pricing and vehicle types',
      solarboxCustomers: 'Paying customers',
      solarboxCustomersDesc: 'Solarbox subscribers and payment history',
      solarboxBikeControl: 'Bike control',
      solarboxBikeControlDesc: 'Lock or unlock a Solarbox bike by QR code',
    },
    overview: {
      title: 'Overview',
      activeDrivers: 'Active Drivers',
      activeDriversDesc:
        'Drivers registered in your organization, excluding those who have never come online.',
      available: 'Available',
      availableDesc:
        'Drivers currently online and available to accept deliveries.',
      offline: 'Offline',
      offlineDesc: 'Registered drivers that are currently offline.',
      delivering: 'Delivering',
      deliveringDesc: 'Drivers currently on an active delivery.',
      totalDeliveries: 'Total Deliveries',
      totalDeliveriesDesc:
        'All-time count of completed deliveries for your organization.',
      thisMonth: 'This Month',
      completed: 'Completed',
      completedDesc:
        'Deliveries completed this month (driver picked up and delivered successfully).',
      total: 'Total',
      totalDesc:
        'All deliveries started this month, regardless of current status.',
      completionRate: 'Completion Rate',
      completionRateDesc:
        'Percentage of deliveries started this month that were completed. Calculated as Completed / Total.',
      noData: 'No delivery data available yet',
      trackDelivery: 'Track Delivery',
      searchPlaceholder: 'Enter quotation ID',
      search: 'Search',
      notFound: 'Delivery not found',
      pickup: 'Pickup',
      destination: 'Destination',
      driver: 'Driver',
      requester: 'Requester',
      recipient: 'Recipient',
      price: 'Price',
      distance: 'Distance',
      duration: 'Duration',
      started: 'Started',
      ended: 'Ended',
      createdAt: 'Created',
      photoProof: 'Photo Proof',
      view: 'View',
      status: 'Status',
      fetchError: 'Failed to load delivery details',
      overviewFetchError: 'Failed to load overview data',
      retry: 'Retry',
      alertsTitle: 'Delivery Alerts',
      stuck: 'Stuck',
      failed: 'Failed',
      stuckFor: 'Stuck for',
      noAlerts: 'No delivery alerts',
      leaderboard: 'Driver Leaderboard',
      byDeliveries: 'By Deliveries',
      byAvailability: 'By Availability',
      availabilityComingSoon: 'Coming soon',
      rank: '#',
      driverName: 'Driver',
      lbDeliveries: 'Deliveries',
      revenue: 'Revenue',
      avgTime: 'Avg Time',
      lbRate: 'Rate',
      week: 'Week',
      month: 'Month',
      allTime: 'All Time',
      noDriverData: 'No driver data available',
      leaderboardError: 'Failed to load leaderboard',
      onlineTime: 'Online Time',
      deliveringTime: 'Delivering',
      daysActive: 'Days Active',
      deliveryTrends: 'Delivery Trends',
      peakHours: 'Peak Hours',
      last7Days: '7 days',
      last30Days: '30 days',
      last90Days: '90 days',
      cancelled: 'Cancelled',
      trendFailed: 'Failed',
      trendDeliveries: 'Deliveries',
      noTrendData: 'No delivery data for this period',
      demandZones: 'Demand Zones',
      demandZonesDesc: 'Areas with the most delivery requests',
      noHotspotData: 'No pickup location data for this period',
      hotspotsFetchError: 'Failed to load hotspot data',
    },
    deliveryDetail: {
      back: 'Back to Deliveries',
      fetchError: 'Failed to load delivery details',
      reassignDriver: 'Reassign Driver',
      cancelDelivery: 'Cancel Delivery',
      cancelDeliveryConfirm: 'Are you sure you want to cancel this delivery?',
      cancelDeliverySuccess: 'Delivery cancelled',
      cancelDeliveryError: 'Failed to cancel delivery',
      cannotCancelDelivery:
        'Cannot cancel a delivery that is already in progress',
    },
    drivers: {
      title: 'Drivers',
      list: 'List',
      map: 'Map',
      name: 'Name',
      phone: 'Phone',
      status: 'Status',
      vehicleType: 'Vehicle Type',
      lastUpdated: 'Last Updated',
      noDrivers: 'No drivers found for this organization',
      available: 'Available',
      offline: 'Offline',
      delivering: 'Delivering',
      all: 'All',
      lastSeen: 'Last seen',
      search: 'Search by name or phone',
      clearFilters: 'Clear filters',
      fetchError: 'Failed to load drivers',
      liveTracking: 'Live Tracking',
      lowRatedDrivers: 'Low-rated drivers',
      lowRatedDriversDescription:
        'The following drivers have an average rating below 3 stars',
      avgRating: 'avg',
    },
    driverDetail: {
      title: 'Driver Details',
      back: 'Back to Drivers',
      profile: 'Profile',
      phone: 'Phone',
      vehicleType: 'Vehicle Type',
      status: 'Status',
      totalDeliveries: 'Total Deliveries',
      totalEarnings: 'Total Earnings',
      averageRating: 'Average Rating',
      ratingsCount: 'Ratings',
      deliveryHistory: 'Delivery History',
      noDeliveries: 'No deliveries found for this driver',
      fetchError: 'Failed to load driver details',
      location: 'Location',
      lastSeen: 'Last seen',
      historyTab: 'History',
      ratingsTab: 'Ratings',
      noRatings: 'No ratings yet',
      ratingDistribution: 'Rating Distribution',
      recentRatings: 'Recent Ratings',
      noComment: 'No comment',
      activityTab: 'Activity',
      onlineTime: 'Online Time',
      deliveringTime: 'Delivering Time',
      daysActive: 'Days Active',
      dailyBreakdown: 'Daily Breakdown',
      noActivityData: 'No activity data available',
      activityError: 'Failed to load activity data',
    },
    deliveries: {
      title: 'Deliveries',
      id: 'ID',
      status: 'Status',
      route: 'Route',
      price: 'Price',
      driver: 'Driver',
      date: 'Date',
      noDeliveries: 'No deliveries found',
      fetchError: 'Failed to load deliveries',
      completed: 'Completed',
      pending: 'Pending',
      accepted: 'Accepted',
      atPickup: 'At Pickup',
      pickedUp: 'Picked Up',
      rejected: 'Rejected',
      cancelled: 'Cancelled',
      noDeliverer: 'No Driver',
      clearFilters: 'Clear filters',
      rateDelivery: 'Rate this delivery',
      ratingLabel: 'Rating',
      commentPlaceholder: 'Add a comment (optional)',
      submitRating: 'Submit Rating',
      ratedOn: 'Rated on',
      ratingError: 'Failed to submit rating',
      dateRange: 'Date range',
      clearDates: 'Clear dates',
    },
    settings: {
      title: 'Settings',
      pricing: 'Pricing',
      pricingDesc: 'Set the delivery price per vehicle type (FCFA)',
      moto: 'Moto',
      tricycle: 'Tricycle',
      vehicleTypes: 'Supported Vehicle Types',
      vehicleTypesDesc:
        'Choose which vehicle types are available for deliveries',
      save: 'Save',
      saving: 'Saving...',
      saveSuccess: 'Settings saved',
      saveError: 'Failed to save settings',
      fetchError: 'Failed to load settings',
    },
    dispatch: {
      title: 'Dispatch',
      subtitle: 'Create a delivery and assign a driver',
      stepDetails: 'Delivery Details',
      stepReview: 'Review Quote',
      stepDriver: 'Assign Driver',
      stepDone: 'Done',
      pickupLocation: 'Pickup location',
      pickupPlaceholder: 'Search for an address',
      dropoffLocation: 'Dropoff location',
      dropoffPlaceholder: 'Search for an address',
      noResults: 'No results',
      geocodingCountry: 'Geocoding country',
      mapHint:
        'Click on the map to drop a pin, or drag the existing pin to adjust.',
      mapPlacePinHint: 'Click anywhere to drop a pin',
      requesterSection: 'Requester (sender)',
      requesterName: 'Requester name',
      requesterPhone: 'Requester phone',
      recipientSection: 'Recipient',
      recipientName: 'Recipient name',
      recipientPhone: 'Recipient phone',
      sameAsRequester: 'Same as requester',
      vehicleType: 'Vehicle type',
      moto: 'Moto',
      tricycle: 'Tricycle',
      instructions: 'Instructions (optional)',
      instructionsPlaceholder: 'Floor, gate code, special handling…',
      back: 'Back',
      next: 'Next',
      getQuote: 'Get quote',
      gettingQuote: 'Getting quote…',
      reviewTitle: 'Review the quote',
      price: 'Price',
      priceOriginal: 'Original price',
      priceOverride: 'Override the price if needed',
      eta: 'Estimated time',
      etaMinutes: 'minutes',
      continueToDriver: 'Continue to driver',
      updatingPrice: 'Updating price…',
      driverTitle: 'Choose a driver',
      availableOnly: 'Available only',
      allDrivers: 'All drivers',
      noAvailableDrivers: 'No available drivers',
      refreshDrivers: 'Refresh',
      selectDriver: 'Select a driver',
      sendProposition: 'Send proposition',
      sending: 'Sending…',
      autoSearch: 'Auto-search',
      searching: 'Searching…',
      propositionSentTitle: 'Proposition sent',
      waitingForDriver: 'Waiting for {name} to accept the delivery…',
      autoSearchPending: 'Searching for available drivers…',
      dispatchFailed: 'Dispatch failed',
      dispatchFailedHelp:
        'No driver picked up this dispatch. You can retry with a different driver or use auto-search.',
      quotationId: 'Delivery ID',
      targetedDriver: 'Targeted driver',
      acceptedDriver: 'Accepted by',
      viewDelivery: 'View delivery',
      newDispatch: 'New dispatch',
      errorQuote: 'Failed to create quotation',
      errorPriceUpdate: 'Failed to update price',
      errorPropose: 'Failed to send proposition',
      errorAutoSearch: 'Failed to start auto-search',
      errorFetchDrivers: 'Failed to fetch drivers',
      errorDriverUnavailable: 'Driver is no longer available',
      errorDriverNoToken: 'Driver is unreachable (no push token)',
      errorRequiredFields: 'Please fill in all required fields',
      errorPhoneFormat: 'Invalid phone number',
      errorMapboxToken: 'Mapbox token not configured',
      listView: 'List',
      mapView: 'Map',
      pickupMarker: 'Pickup',
      stillWaiting: 'Still waiting for driver response...',
      stillSearching: 'Still searching for an available driver...',
    },
    solarbox: {
      title: 'Paying customers',
      phoneNumber: 'Phone number',
      displayName: 'Name',
      customerSince: 'Customer since',
      lastPaid: 'Last paid',
      noCustomers: 'No paying customers yet',
      fetchError: 'Failed to load customers',
      detail: {
        title: 'Customer details',
        phoneNumberLabel: 'Phone',
        displayNameLabel: 'Display name',
        displayNamePlaceholder: 'e.g. Mamadou D.',
        displayNameTooLong: 'Max 80 characters',
        saveButton: 'Save',
        clearButton: 'Clear',
        labelSetBy: 'set by {email} · {date}',
        registeredAt: 'Customer since',
        lastPaidAt: 'Last paid',
        recentActivityHeading: 'Recent activity',
        noActivity: 'No activity in the last 90 days',
        activityOtherGroup: 'Other activity',
        activityFailureBadge: 'failure',
        activityEventCount: '{count} events',
        activityExpandAria: 'Expand {label}',
        activityCollapseAria: 'Collapse {label}',
        loadError: 'Failed to load customer details',
        saveError: 'Save failed',
      },
      bikeControl: {
        title: 'Bike control',
        subtitle: 'Lock or unlock a Solarbox bike by QR code.',
        qrIdLabel: 'QR code',
        qrIdHint: 'e.g. SB10057',
        paymentReferenceLabel: 'Payment reference (optional)',
        paymentReferenceHint:
          "Attributes the action to the customer's user in the audit log.",
        reasonLabel: 'Reason (optional)',
        reasonHint: 'Surfaced in the activity event.',
        reasonPlaceholder: 'e.g. customer reported stuck bike',
        lockButton: 'Lock',
        unlockButton: 'Unlock',
        confirmTitle: 'Confirm action',
        confirmBody: 'Are you sure you want to {action} {qrId}?',
        confirmCancel: 'Cancel',
        confirmDispatch: 'Dispatch',
        resultSucceeded: 'Bike action succeeded',
        resultFailed: 'Bike action failed',
        resultQrLabel: 'QR',
        resultDeviceLabel: 'Device ID',
        resultErrorLabel: 'Error',
        resetButton: 'New action',
        requestError: 'Failed to dispatch the bike action',
      },
    },
  },
  imminote: {
    modules: {
      parametres: 'Settings',
      parametresDesc: 'Manage API keys and authentication',
      messages: 'Messages',
      messagesDesc: 'Send templates and view history',
      apiDocs: 'API Documentation',
      apiDocsDesc: 'Explore the API reference and integration guides',
    },
    tabs: {
      integration: 'Integration',
      integrationDesc: 'Interactive code snippet generator',
      api: 'API',
      apiDesc: 'API endpoint documentation',
      createTemplate: 'Create Template',
      scheduled: 'Scheduled',
      scheduledDesc: 'View and manage scheduled messages',
      history: 'History',
      historyDesc: 'Message history and API usage stats',
    },
    integration: {
      description:
        'Generate code snippets to integrate the Imminote API into your application',
      selectTemplate: 'Select a Template',
      template: 'Template',
      apiConfig: 'API Configuration',
      apiConfigDesc: 'Configure the request parameters for the API call',
      apiKey: 'API Key',
      apiKeyHelp: 'Your Imminote API key from Settings',
      recipients: 'Recipients',
      recipientsHelp: 'Select a country code and enter the local number',
      add: 'Add',
      phoneNumberId: 'Phone Number ID',
      scheduleMessage: 'Schedule for later',
      scheduledSendTime: 'Send at',
      parameters: 'Template Parameters',
      parametersDesc:
        'Update these example values to see them reflected in the code snippet',
      headerParams: 'Header Parameters',
      bodyParams: 'Body Parameters',
      codeSnippet: 'Code Snippet',
      copied: 'Code copied to clipboard',
      selectTemplatePrompt: 'Select a template above to generate code snippets',
    },
    apiDocs: {
      description:
        'Complete API reference for sending WhatsApp template messages',
      endpoint: 'Endpoint',
      authentication: 'Authentication',
      authDesc:
        'All requests must include an API key in the Authorization header',
      rateLimits: 'Rate Limits',
      currentPlan: 'Your current plan',
      rateLimitsDesc:
        'The maximum number of recipients per API call is based on your subscription plan',
      maxRecipients: 'Max Recipients per Request',
      upgradePrompt:
        'Need to send to more recipients? Upgrade your plan in Settings.',
      requestFormat: 'Request Format',
      requestFormatDesc: 'JSON request body structure',
      field: 'Field',
      type: 'Type',
      required: 'Required',
      fieldDescription: 'Description',
      recipientsDesc: 'Array of phone numbers in E.164 format',
      phoneNumberIdDesc: 'Your WhatsApp Business phone number ID',
      templateNameDesc: 'Name of the approved template to send',
      languageDesc: 'Template language code (e.g., "en", "fr")',
      componentsDesc: 'Template parameters (header, body, buttons)',
      example: 'Example Request',
      responseFormat: 'Response Format',
      successResponse: 'Success Response (200)',
      errorResponse: 'Error Response (429)',
      errorCodes: 'Error Codes',
      code: 'Code',
      code200: 'Success - Messages sent successfully',
      code400: 'Bad Request - Invalid request parameters',
      code401: 'Unauthorized - Invalid or missing API key',
      code403: 'Forbidden - Template not available or subscription inactive',
      code404: 'Not Found - Template or organization not found',
      code429: 'Rate Limit Exceeded - Too many recipients for your plan',
      code500: 'Internal Server Error',
      scheduling: 'Message Scheduling',
      schedulingDesc:
        'Schedule messages for future delivery by adding the scheduled_send_time field to your send request.',
      scheduledSendTimeDesc:
        'ISO 8601 date for scheduled delivery. If omitted, message is sent immediately.',
      schedulingRequest: 'Schedule a Message',
      schedulingResponse: 'Schedule Response (201)',
      listScheduled: 'List Scheduled Messages',
      cancelScheduled: 'Cancel a Scheduled Message',
    },
    scheduled: {
      description:
        'View and manage scheduled messages created via the Imminote API',
      title: 'Scheduled Messages',
      allStatuses: 'All Statuses',
      statusScheduled: 'Scheduled',
      statusSent: 'Sent',
      statusCanceled: 'Canceled',
      statusFailed: 'Failed',
      templateColumn: 'Template',
      recipientsColumn: 'Recipients',
      scheduledForColumn: 'Scheduled For',
      statusColumn: 'Status',
      createdColumn: 'Created',
      actionsColumn: 'Actions',
      cancel: 'Cancel',
      noMessages: 'No scheduled messages',
      noMessagesDesc:
        'Messages scheduled via the Imminote API will appear here',
    },
    apiKeys: {
      title: 'API Keys',
      description:
        'Manage API keys to authenticate requests to the Imminote API',
      generate: 'Generate New Key',
      revoke: 'Revoke',
      active: 'Active',
      revoked: 'Revoked',
      revokedKeys: 'Revoked Keys',
      revokedOn: 'Revoked',
      keyColumn: 'Key',
      nameColumn: 'Name',
      createdColumn: 'Created',
      lastUsedColumn: 'Last Used',
      statusColumn: 'Status',
      actionsColumn: 'Actions',
      neverUsed: 'Never',
      noKeys: 'No API keys yet',
      generateFirst: 'Generate your first API key to get started',
      generateTitle: 'Generate New API Key',
      generateButton: 'Generate',
      nameLabel: 'Key Name (optional)',
      namePlaceholder: 'e.g., Production Server',
      nameDescription:
        'Optionally give your API key a name to help identify it later',
      saveWarning:
        "Make sure to copy your API key now. You won't be able to see it again!",
      generated: 'API key generated successfully',
      copied: 'API key copied to clipboard',
      confirmRevoke:
        'Are you sure you want to revoke this API key? This action cannot be undone.',
      fetchError: 'Failed to fetch API keys',
      generateError: 'Failed to generate API key',
      revokeError: 'Failed to revoke API key',
      copyError: 'Failed to copy API key',
    },
    history: {
      description: 'View message history and API usage statistics',
      messageHistory: 'Message History',
      apiUsageStats: 'API Usage Statistics',
      filters: 'Filters',
      status: 'Status',
      template: 'Template',
      allStatuses: 'All Statuses',
      allTemplates: 'All Templates',
      recipientColumn: 'Recipient',
      templateColumn: 'Template',
      statusColumn: 'Status',
      sentAtColumn: 'Sent',
      noMessages: 'No messages yet',
      noMessagesDesc: 'Messages sent via the Imminote API will appear here',
      overallStats: 'Overall Statistics',
      last30Days: 'Last 30 Days',
      last7Days: 'Last 7 Days',
      totalCalls: 'Total API Calls',
      successfulCalls: 'Successful Calls',
      failedCalls: 'Failed Calls',
      totalRecipients: 'Total Recipients',
      callsByApiKey: 'Calls by API Key',
      callsOverTime: 'API Calls Over Time',
      mostUsedTemplates: 'Most Used Templates',
      keyColumn: 'API Key',
      callsColumn: 'Calls',
      recipientsColumn: 'Recipients',
      successRateColumn: 'Success Rate',
      templateNameColumn: 'Template',
      usageCountColumn: 'Usage Count',
      noApiKeys: 'No API key data',
      noTemplateUsage: 'No template usage data',
      successRate: 'Success Rate',
      noUsageData: 'No API usage data yet',
      noUsageDataDescription:
        'Usage statistics will appear here once you start making API calls',
    },
  },
  sdk: {
    comingSoon: 'Coming soon...',
  },
  botFlows: {
    // List page
    title: 'Bot Flows',
    description: 'Build and manage chatbot flows',
    subtitle: 'Create and manage automated conversation flows for your chatbot',
    createNew: 'Create Flow',
    emptyTitle: 'No flows yet',
    emptyDescription:
      'Create your first bot flow to start automating conversations',
    createFirst: 'Create First Flow',
    published: 'Published',
    draft: 'Draft',
    lastModified: 'Last modified',
    flowName: 'Flow Name',
    // Templates
    selectTemplate: 'Choose a template',
    blankTemplate: 'Blank',
    blankTemplateDesc: 'Start from scratch with an empty flow',
    customerSupportTemplate: 'Customer Support',
    customerSupportTemplateDesc:
      'Handle orders, returns, and general inquiries',
    faqBotTemplate: 'FAQ Bot',
    faqBotTemplateDesc: 'Answer common questions with a menu-based flow',
    appointmentTemplate: 'Appointment Booking',
    appointmentTemplateDesc: 'Collect info and schedule appointments',
    // Editor
    unsavedChanges: 'Unsaved changes',
    test: 'Test',
    publish: 'Publish',
    steps: 'Steps',
    stepsOrderInfo:
      'Drag to reorder. Order is for organization only and does not affect flow logic.',
    searchSteps: 'Search steps...',
    noSearchResults: 'No steps match your search',
    addStep: 'Add Step',
    noSteps: 'No steps yet. Add your first step to begin building the flow.',
    addFirstStep: 'Add First Step',
    selectStep: 'Select a step to edit',
    editStep: 'Edit Step',
    stepName: 'Step Name',
    // Action types
    actionType: 'Action Type',
    actionTypes: {
      buttons: 'Buttons',
      text: 'Text Message',
      list: 'List Menu',
      locationRequest: 'Request Location',
      mediaRequest: 'Request Media',
      whatsappFlow: 'WhatsApp Flow',
      end: 'End Conversation',
    },
    // Message content
    messageContent: 'Message Content',
    messageBody: 'Message Body',
    messageBodyPlaceholder: 'Enter the message to send...',
    buttons: 'Buttons',
    addButton: 'Add Button',
    autoAdvanceTo: 'Auto-advance to',
    // Transitions
    transitions: 'Transitions',
    addTransitionFor: 'Add transition for',
    addResponseHandler: 'Add Response Handler',
    onLocationReceived: 'On location received',
    onMediaReceived: 'On media received',
    // Fallback
    fallback: 'Fallback Behavior',
    fallbackDescription:
      'What happens when the user sends an unexpected message',
    fallbackMessage: 'Fallback Message',
    fallbackAction: 'Fallback Action',
    repeatStep: 'Repeat this step',
    goToStep: 'Go to step',
    // Entry point
    entryPoint: 'Entry',
    setAsEntry: 'Set as Entry Point',
    duplicateStep: 'Duplicate',
    stepNotReachable: 'Step is not reachable from entry point',
    // Toasts
    loadError: 'Failed to load flow',
    saveSuccess: 'Flow saved successfully',
    saveError: 'Failed to save flow',
    publishSuccess: 'Flow published successfully',
    publishValidationError: 'Cannot publish: flow has validation errors',
    publishError: 'Failed to publish flow',
    publishNoPhoneNumber: 'Please assign a phone number before publishing',
    // Phone number
    phoneNumber: 'Phone Number',
    noPhoneNumber: 'Not assigned',
    phoneNumberUpdated: 'Phone number updated',
    phoneNumberUpdateError: 'Failed to update phone number',
    createError: 'Failed to create flow',
    deleteSuccess: 'Flow deleted successfully',
    deleteError: 'Failed to delete flow',
    duplicateSuccess: 'Flow duplicated successfully',
    duplicateError: 'Failed to duplicate flow',
    compileError: 'Failed to compile flow',
    compileValidationError:
      'Cannot compile: Please ensure all transitions have a target step selected',
    yamlCopied: 'YAML copied to clipboard',
    // YAML
    viewYaml: 'View YAML',
    // Validation errors
    validationErrorsTitle: 'Validation Errors',
    validationErrorsDescription:
      'Please fix the following issues before compiling:',
    clickToEdit: 'Click to edit',
    // Publish confirmation
    publishConfirmTitle: 'Publish Flow',
    publishConfirmMessage:
      'Are you sure you want to publish this flow? It will become active immediately.',
    // Simulator
    simulatorSubtitle: 'Test Mode',
    conversationEnded: 'Conversation ended',
    typeMessage: 'Type a message...',
    restartSimulation: 'Restart',
    // View modes
    listView: 'List View',
    graphView: 'Graph View',
  },
  users: {
    users: 'Users',
    viewUsers: 'View Users',
    search: 'Search users...',
    searchResultsEmpty: 'No users found...',
    admin: 'admin',
    created: 'Created:',
    lastUpdated: 'Last updated:',
    grantAdminPrivileges: 'Grant admin privileges',
    revokeAdminPrivileges: 'Revoke admin privileges',
    addUsers: 'Add Users',
    bulkAddUsersToOrg: 'Bulk Add Users to Organization',
    addUserToOrg: 'Add User to Organization',
    email: 'Email',
    isAdmin: 'Is admin',
    shouldSendEmail: 'Send email invitation',
    uploadCsv: 'Upload CSV',
    addUser: 'Add User',
    noUsersYet: 'This organization has not added any users yet.',
    userRoleSuccessfullyUdated: 'Successfully updated user role!',
    errorUpdatingUserRole: 'Error updating user role...',
    bulkUsersAddedSuccessfully: 'Users added in bulk sucessfully!',
    errorAddingUsersInBulk: 'Error adding users in bulk...',
    userAddedSuccessfully: 'User added to organization sucessfully!',
    errorAddingUser: 'Error adding user to organization...',
    successfullyAdded: 'Successfully added',
    usersToOrg: 'users to organization.',
    uploading: 'Uploading...',
    csvUploadError: 'Failed to upload CSV. Please try again.',
    selectCsvToAdd: 'Upload CSV to add users',
    csvHeaders: 'The CSV should have a header row with the following columns:',
    csvRequiredColumns:
      'Both username (email) and role are required. Role must be "admin" or "user".',
  },
  modals: {
    confirmDeleteTitle: 'Confirm Deletion',
    deleteConfirmation: 'Are you sure?',
    cancel: 'Cancel',
    delete: 'Delete',
  },
  common: {
    uploadFile: 'Upload File',
    dragDropFile: 'Drag & drop a file here, or click to browse',
    readyToUpload: 'Ready to upload',
    noName: '<no name>',
    clientNotAvailable: 'This client is not available',
    groupNotAvailable: 'This group is not available',
    userNotAvailable: 'This user is not available',
    templateNotAvailable: 'This template is not available',
    goBack: 'Go Back',
    confirm: 'Confirm',
    confirmCancellation: 'Confirm Cancellation',
    upgradeRequired: 'Upgrade Required',
    upgradeToAccess:
      'To access this feature, please upgrade your subscription plan.',
    viewPlans: 'View Plans',
    gotIt: 'Got it',
    enterpriseFeature: 'Enterprise Feature',
    watchDemo: 'Watch Demo',
    hideDemo: 'Hide Demo',
    save: 'Save',
    cancel: 'Cancel',
    create: 'Create',
    retry: 'Retry',
    creating: 'Creating...',
    delete: 'Delete',
    duplicate: 'Duplicate',
    copy: 'Copy',
    close: 'Close',
    beta: 'Beta',
  },
  refresh: {
    lastUpdated: 'Last updated',
    refreshing: 'Refreshing...',
    refreshNow: 'Refresh',
    justNow: 'just now',
    minutesAgo: 'm ago',
    hoursAgo: 'h ago',
    daysAgo: 'd ago',
    never: 'never',
  },
  demos: {
    title: 'Product Demos',
    subtitle:
      'Watch interactive demos to see how our platform can help your business.',
    uiNotice:
      'These demos show an earlier version of our interface. The current platform has a refreshed, improved design.',
    backToHome: 'Back to Home',
    comingSoon: 'Demos coming soon. Check back later!',
    viewDemos: 'View Demos',
  },
  faq: {
    title: 'Frequently Asked Questions',
    subtitle:
      'Find answers to common questions about our platform, WhatsApp Business, and Meta policies.',
    viewAllFaqs: 'View all FAQs',
    sections: [
      {
        title: 'Message Delivery',
        questions: [
          {
            question: 'Why did my message fail to deliver?',
            answer:
              'Messages can fail for several reasons:\n\n• The recipient has blocked your business number\n• The phone number is invalid or no longer active on WhatsApp\n• You tried to send a free-form message outside the 24-hour customer service window\n• Your template was rejected or paused by Meta\n• The recipient has not opted in to receive messages from your business\n\nCheck the message status in your dashboard for specific error details.',
          },
          {
            question: 'What is the 24-hour customer service window?',
            answer:
              'When a customer messages your business, a 24-hour window opens during which you can send free-form messages (text, images, documents) without using approved templates. After this window closes, you must use pre-approved message templates to initiate new conversations. This is a Meta policy designed to prevent spam.',
          },
          {
            question:
              'What is the difference between Delivered and Read status?',
            answer:
              "• Sent: The message has left our servers\n• Delivered: The message reached the recipient's device\n• Read: The recipient opened and viewed the message (only shown if they have read receipts enabled)\n\nNote: Some users disable read receipts, so a message may be read without showing the 'Read' status.",
          },
          {
            question: 'Can I guarantee message delivery?',
            answer:
              'No platform can guarantee 100% message delivery. Delivery depends on factors outside our control, including:\n\n• Whether the recipient has WhatsApp installed\n• Network connectivity\n• Meta/WhatsApp server availability\n• Recipient privacy settings\n\nWe provide delivery receipts so you can track which messages were successfully delivered.',
          },
          {
            question: 'Why do marketing messages have lower delivery rates?',
            answer:
              'Meta limits how many marketing messages each WhatsApp user can receive across all businesses. This means:\n\n• A user who already receives many marketing messages from other businesses may not receive yours\n• Meta may choose not to deliver your message to protect user experience (error 131049)\n• Users can individually opt out of marketing messages from your business (error 131050)\n\nTo improve delivery rates:\n\n• Send to contacts who have previously messaged your business\n• Keep marketing messages relevant and infrequent\n• Use Utility templates for transactional messages (order updates, reminders) — these are not subject to marketing limits',
          },
        ],
      },
      {
        title: 'Meta Policies & Compliance',
        questions: [
          {
            question: 'Who decides if my messages are delivered?',
            answer:
              "Meta (WhatsApp's parent company) has the final say on message delivery. They operate spam detection systems that may block or limit messages they consider unwanted. As a WhatsApp Business Solution Provider, we facilitate message sending, but Meta ultimately controls the WhatsApp platform and enforces their policies.",
          },
          {
            question: 'What types of messages does Meta prohibit?',
            answer:
              'Meta prohibits:\n\n• Spam or unsolicited bulk messaging\n• Messages without proper user opt-in\n• Misleading or deceptive content\n• Adult content or illegal products/services\n• Messages that violate WhatsApp Commerce Policy\n• Harassment or threatening messages\n\nViolating these policies can result in your number being banned.',
          },
          {
            question: 'How does the template approval process work?',
            answer:
              'When you create a message template:\n\n1. Submit the template through our platform\n2. Meta reviews it (usually within minutes to 24 hours)\n3. Templates are approved, rejected, or flagged for review\n\nCommon rejection reasons include promotional content in utility templates, missing variable placeholders, or policy violations. You can edit and resubmit rejected templates.',
          },
          {
            question:
              'What is the difference between Marketing, Utility, and Authentication templates?',
            answer:
              '• Marketing: Promotional messages, offers, newsletters, product announcements. Requires explicit opt-in.\n\n• Utility: Transactional messages like order confirmations, shipping updates, appointment reminders. Related to an existing transaction.\n\n• Authentication: One-time passwords (OTPs) and verification codes only.\n\nEach category has different pricing and approval criteria.',
          },
          {
            question: 'What happens if my WhatsApp number gets banned?',
            answer:
              "If Meta bans your number, you'll lose the ability to send messages from that number. Common causes include:\n\n• High spam reports from recipients\n• Sending to users who haven't opted in\n• Template policy violations\n• Abnormal messaging patterns\n\nYou can appeal through Meta's Business Help Center, but reinstatement is not guaranteed. We recommend maintaining good messaging practices to avoid bans.",
          },
          {
            question: 'Do I need opt-in consent from recipients?',
            answer:
              "Yes, Meta requires that you obtain opt-in consent before sending WhatsApp messages to customers. This means:\n\n• Customers must actively agree to receive messages\n• You must clearly explain what messages they'll receive\n• You must provide an easy way to opt out\n\nKeep records of opt-in consent. Sending messages without consent violates Meta's policies and can result in your number being restricted or banned.",
          },
        ],
      },
      {
        title: 'Pricing & Billing',
        questions: [
          {
            question: 'How does the trial work?',
            answer:
              "There is a 14-day free trial. You'll get full access to all features in the basic plan until the trial period expires. No credit card or commitment required.",
          },
          {
            question: 'Can I change plans later?',
            answer:
              'Yes, you can upgrade or downgrade your plan at any time. Your new plan and its features will be active starting your next billing period. Your current plan remains active until then.',
          },
          {
            question: 'What payment methods do you accept?',
            answer:
              'We accept card payments, Orange Money, and Wave Money. Enterprise customers can explore additional payment options.',
          },
          {
            question: 'What happens when I exceed my plan limits?',
            answer:
              "We'll notify you when you're approaching your limits. You can upgrade to a higher plan or contact us for a custom solution.",
          },
          {
            question: 'Do you offer refunds?',
            answer:
              "You can cancel your subscription at any time from your profile settings. If you have concerns or issues, contact our support team - we're happy to discuss your situation and explore options on a case-by-case basis.",
          },
          {
            question: 'How does Dabsi pricing work?',
            answer:
              "We offer flexible pricing options to suit your needs:\n\n• Pay As You Go: Pay per message sent. Template messages (Marketing, Utility, Authentication) are charged separately by type, and non-template messages are charged individually. Rates vary by recipient's country.\n\n• Monthly Plans: Fixed monthly cost with included message credits and additional features.\n\nPrices are subject to change. Check our pricing page for current rates and plan details.",
          },
          {
            question: 'Do you offer discounts for non-profits?',
            answer:
              'Yes, we offer special pricing for registered non-profit organizations. Contact our sales team for details.',
          },
        ],
      },
      {
        title: 'Technical & API',
        questions: [
          {
            question: 'What are the API rate limits?',
            answer:
              'Plans with full API access (Pay as you go, Professional, and Enterprise) include a rate limit of 100 messages per second. This applies to the Send API for sending WhatsApp messages programmatically. If you need higher throughput, contact our sales team for a custom solution.',
          },
          {
            question: 'How reliable are webhooks?',
            answer:
              'We deliver webhooks with at-least-once delivery guarantee. This means:\n\n• Webhooks may occasionally be delivered more than once\n• Your endpoint should handle duplicate events (use message IDs for deduplication)\n• If your endpoint is down, we retry with exponential backoff (up to 3 attempts)\n\nEnsure your webhook endpoint responds with a 200 status quickly to avoid timeouts.',
          },
          {
            question: 'What phone number do I need for WhatsApp Business?',
            answer:
              'You need a phone number that:\n\n• Can receive SMS or voice calls for verification\n• Is not already registered on WhatsApp or WhatsApp Business app\n• You have ownership/control over\n\nYou can use a landline (verified via voice call) or mobile number. Once registered with the API, you cannot use that number with the regular WhatsApp apps.',
          },
          {
            question: 'Can I use multiple phone numbers?',
            answer:
              'Yes, depending on your plan. Professional and Enterprise plans support multiple phone numbers. Each number requires separate verification and can have its own display name and profile. This is useful for different brands, departments, or regions.',
          },
        ],
      },
      {
        title: 'Account & Setup',
        questions: [
          {
            question: 'How do I get started with WhatsApp Business API?',
            answer:
              '1. Sign up for a Dabsi account\n2. Connect your Facebook Business account\n3. Create or select a WhatsApp Business Account (WABA)\n4. Add and verify your phone number\n5. Complete business verification (if required)\n6. Start creating templates and sending messages\n\nThe embedded signup process and demo videos guide you through each step.',
          },
          {
            question: 'What is business verification and do I need it?',
            answer:
              "Business verification confirms your business identity with Meta. It's required to:\n\n• Send more than 250 messages per day\n• Access certain features\n• Display a verified business name\n\nVerification requires submitting business documents (registration, utility bills, etc.) and typically takes 2-5 business days.",
          },
          {
            question: 'What display name guidelines should I follow?',
            answer:
              'Your WhatsApp display name must:\n\n• Represent your business accurately\n• Not contain misleading information\n• Not include generic words like "Marketing" or "Notifications" alone\n• Not violate trademarks\n• Be consistent with your business documentation\n\nMeta reviews display names and may reject those that don\'t meet guidelines.',
          },
          {
            question:
              'What happens to my data when I downgrade or cancel my subscription?',
            answer:
              "When you downgrade, excess clients, groups, and templates beyond your new plan's limits are set to read-only mode. You can still view all data, but can't send messages to excess items until you upgrade again.\n\nWhen you cancel, your data is retained for 30 days in case you reactivate. After 30 days, data may be permanently deleted according to our data retention policy.",
          },
        ],
      },
      {
        title: 'Data Privacy & GDPR',
        questions: [
          {
            question: 'What happens when I delete a client?',
            answer:
              'When you delete a client from your organization, the client is soft-deleted — meaning the record is hidden from your active view but the data remains in the system. This allows you to restore the client if the deletion was accidental.\n\nSoft-deleted data is not permanently erased unless you explicitly submit a data erasure request.',
          },
          {
            question: 'What is a data erasure request?',
            answer:
              'A data erasure request is a formal request to permanently and irreversibly delete all personally identifiable information (PII) associated with a client or user. This includes:\n\n• Client details (name, phone number, email)\n• Message content and phone numbers in message history\n• Scheduled message parameters\n• Related audit log entries\n\nOnce executed, the data cannot be recovered. Message records are preserved for analytics purposes but with all PII replaced by [REDACTED].',
          },
          {
            question: 'How does the erasure grace period work?',
            answer:
              'When an admin submits a data erasure request, it enters a 30-day grace period. During this time:\n\n• The request is marked as pending\n• An admin can cancel the request at any time\n• No data is deleted yet\n\nAfter the 30-day grace period expires, the erasure is automatically executed by our system. This grace period protects against accidental or premature data deletion.',
          },
          {
            question: 'Can I request immediate data erasure?',
            answer:
              'Yes, organization superadmins can force immediate erasure, bypassing the 30-day grace period. This requires explicit confirmation and is intended for cases where data must be removed urgently (e.g., a formal GDPR erasure request from a data subject).\n\nImmediate erasure is irreversible — the data is permanently deleted as soon as it is executed.',
          },
          {
            question: 'Who can submit or cancel an erasure request?',
            answer:
              'Only organization admins and superadmins can submit data erasure requests. Admins can also cancel pending requests during the grace period.\n\nImmediate (forced) erasure without the grace period is restricted to superadmins only.',
          },
          {
            question: 'How is my data secured?',
            answer:
              'Your data is protected by multiple layers of security: encryption at rest and in transit, role-based access controls, secure authentication, and a full audit trail of all data access and modifications. We conduct regular security reviews and follow industry best practices for application and infrastructure security.',
          },
          {
            question: 'Does Dabsi comply with GDPR?',
            answer:
              'Yes. Our platform supports the GDPR right to erasure (Article 17) through our data erasure request system. When a data subject requests deletion of their personal data, an admin can submit an erasure request that permanently removes all PII from our systems.\n\nWe also comply with Senegalese data protection law (Loi n°2008-12). All erasure actions are logged for audit and compliance purposes. Contact us at contact@dabsi.io for any data protection inquiries.',
          },
        ],
      },
    ],
  },
  privacy: {
    title: 'Privacy Policy',
    intro:
      'This Privacy Policy applies to the Dabsi platform. We are committed to protecting your privacy and ensuring your data is handled responsibly.',
    sections: {
      introduction: {
        title: 'Introduction',
        content:
          'We value your privacy, especially when conducting business online. This Privacy Policy outlines how we handle information for users of our platform, including visitors and registered customers.',
      },
      roles: {
        title: 'Data Processing Roles',
        content:
          'The data we store depends on the service you use. For relay and forwarding services (where messages are forwarded to your backend systems), you act as the Data Controller and DABSI acts as a Data Processor, retaining minimal transient data. For managed products like Clientell, we store client information (names, phone numbers, message history) as necessary to provide the service.',
      },
      pii: {
        title: 'What is Personally Identifiable Information?',
        content:
          '"Personally Identifiable Information" refers to any information that can identify, contact, or locate an individual, such as a name, address, phone number, email address, or IP address. It does not include information collected anonymously or demographic data not linked to an identified individual.',
      },
      collect: {
        title: 'Information We Collect',
        content:
          'We may collect basic profile information from all visitors and the following additional information from registered customers: name, email address, phone number, IP address, and business information such as the nature and size of the business.',
      },
      messageContent: {
        title: 'Message Content',
        content:
          'When you use our platform to send WhatsApp messages, we store message templates, recipient information, and delivery status. Message content is used solely to facilitate message delivery and provide analytics. We do not share message content with third parties except as required to deliver messages through the WhatsApp Business API.',
      },
      thirdPartyServices: {
        title: 'Third-Party Services',
        content:
          'We use the following third-party services to operate our platform: Auth0 for authentication, Bictorys for payment processing, and Meta/WhatsApp Business API for message delivery. Each of these services has their own privacy policies that govern their handling of your data.',
      },
      thirdParty: {
        title: 'Third-Party Collection of Information',
        content:
          'Our third-party service vendors may collect information to provide their services. While we request that these third parties protect your information, we do not control their practices.',
      },
      howWeUse: {
        title: 'How We Use Personally Identifiable Information',
        content:
          'We use Personally Identifiable Information to: customize the platform and offer relevant services, fulfill service requests, contact users about platform-related information, and respond to inquiries.',
      },
      sharing: {
        title: 'Information Sharing',
        content:
          'We do not sell your personal information. We may share aggregated, non-specific demographic data with third-party vendors for analytics purposes.',
      },
      hosting: {
        title: 'Hosting and Data Location',
        content:
          'Our services are hosted on secure cloud infrastructure. Data may be processed and stored in data centers located outside of Senegal in accordance with applicable regulations. We ensure appropriate safeguards are in place for any international data transfers.',
      },
      storage: {
        title: 'Data Storage',
        content:
          'Personally Identifiable Information collected by our platform is securely stored and is accessible only to our employees on a need-to-know basis.',
      },
      choices: {
        title: 'Your Choices',
        content:
          'You can opt out of receiving communications by following the instructions in our emails or by contacting us directly.',
      },
      cookies: {
        title: 'Use of Cookies',
        content:
          'We may use cookies to gather information about user preferences and to help secure the platform by automatically logging out inactive users.',
      },
      security: {
        title: 'Security Measures',
        content:
          'We implement industry-standard security measures to protect your data. All data is encrypted at rest and in transit. Access to our systems is controlled through role-based permissions, secure authentication, and comprehensive audit logging that records who accessed what data and when.',
      },
      userRights: {
        title: 'Your Rights',
        content:
          'In accordance with Senegalese law n°2008-12 on the protection of personal data, you have the right to access, rectify, and delete your personal information. To exercise these rights, please contact us using the information provided below.',
      },
      corrections: {
        title: 'Correcting Information',
        content:
          'Users may update or correct their Personally Identifiable Information by contacting us or through their account settings.',
      },
      deletion: {
        title: 'Deleting Information',
        content:
          'You or your organization administrator can request permanent deletion of Personally Identifiable Information through our data erasure system. Erasure requests enter a 30-day grace period before execution, during which they can be canceled. Once executed, all PII is permanently removed from our active systems. Message records are anonymized (PII replaced with [REDACTED]) to preserve non-identifying analytics data. Some residual data may remain in encrypted backups for a limited period.',
      },
      retention: {
        title: 'Data Retention',
        content:
          'We retain your data for as long as necessary to provide our services. Upon termination of your contract or account deletion, your data will be retained in a soft-deleted state. Permanent data erasure can be requested by an organization administrator through our erasure request system, subject to the 30-day grace period. We comply with GDPR right to erasure (Article 17) and Senegalese data protection law (Loi n°2008-12).',
      },
      changes: {
        title: 'Changes to the Privacy Policy',
        content:
          'We will notify users of Privacy Policy changes by posting updates on the platform. Significant changes that affect previously collected information will be communicated directly.',
      },
      contact: {
        title: 'Contact Us',
        content:
          'If you have any questions about this Privacy Policy or wish to exercise your rights regarding your personal information, please contact us at contact@dabsi.io.',
      },
    },
  },
  terms: {
    title: 'Terms of Service',
    intro:
      'This Terms of Service applies to the Dabsi platform. By using our platform, you agree to these terms.',
    sections: {
      agreement: {
        title: 'Agreement',
        content:
          'By accessing or using dabsi.io or any of its subdomains, you confirm that you have the authority to act on behalf of yourself or any entity you represent. Your use of the platform constitutes your acknowledgment that you have read and agreed to these Terms of Service and our Privacy Policy.',
      },
      parties: {
        title: 'Parties to the Agreement',
        content:
          'This agreement is between the users — which includes all individuals who access, view, use, or interact with the platform — and Dabsi, the owner and operator of the platform.',
      },
      serviceNature: {
        title: 'Nature of Service',
        content:
          "DABSI acts as a technology provider facilitating access to third-party platforms, including Meta's WhatsApp Business API. DABSI does not control Meta's infrastructure, WhatsApp's policies, or the availability of third-party services. Service interruptions or changes imposed by third parties are beyond our control.",
      },
      disclaimer: {
        title: 'Disclaimer for Platform Content',
        content:
          'The platform disclaims any responsibility for the accuracy of content displayed on, linked to, or mentioned on this site. Users assume all risks associated with using this information. We also assume no responsibility for any damage to user devices or data.',
      },
      metaDisclaimer: {
        title: 'Disclaimer for Meta',
        content:
          "This platform is not affiliated with, endorsed by, or associated with Meta or Meta Platforms in any way. Users must comply with Meta's and WhatsApp's terms of service when using our platform to send messages.",
      },
      serviceAvailability: {
        title: 'Service Availability',
        content:
          'DABSI provides its services with reasonable efforts but cannot guarantee continuous, uninterrupted availability. The platform may experience downtime for maintenance, updates, or circumstances beyond our control. Service Level Agreements (SLAs) may be available for enterprise customers. We are not liable for any losses resulting from service interruptions.',
      },
      liability: {
        title: 'Limitation of Liability',
        content:
          'By using this platform, you agree to limit claims for damages. DABSI shall not be held liable for indirect losses or interruptions caused by third-party services. In jurisdictions that do not permit such exclusions, our maximum liability will be limited to the amount you paid for using our services.',
      },
      indemnification: {
        title: 'Indemnification',
        content:
          'You agree to indemnify and defend the platform against any claims or damages arising from your actions that cause harm to the platform or third parties.',
      },
      registration: {
        title: 'User Registration',
        content:
          'You may need to register to use the platform. You are responsible for the accuracy of the information provided during registration. You agree to keep your password confidential and are responsible for all activities under your account.',
      },
      ip: {
        title: 'Intellectual Property Rights',
        content:
          'Unless stated otherwise, the platform and all content are the property of Dabsi or its licensors and are protected by intellectual property laws. You are granted a limited license to access and use the platform for its intended purposes.',
      },
      management: {
        title: 'Platform Management',
        content:
          'We reserve the right to monitor the platform for compliance with these Terms, take legal action against violations, and manage the platform to protect our rights and ensure proper functionality.',
      },
      termination: {
        title: 'Account Suspension and Termination',
        content:
          'These Terms remain in effect while you use the platform. DABSI may suspend or terminate any account for non-compliance with these Terms, violation of applicable laws, or any other reason at our discretion.',
      },
      subscriptions: {
        title: 'Pricing and Payments',
        content:
          'Services are billed according to the pricing model applicable to your plan, including pay-as-you-go options. By using paid services, you agree to pay the applicable fees. Prices may change due to adjustments imposed by third-party providers such as Meta.',
      },
      priceModification: {
        title: 'Price Modifications',
        content:
          'DABSI reserves the right to adjust its prices with reasonable notice. Price changes may result from changes in third-party costs, market conditions, or service improvements. Continued use of the platform after price changes constitutes acceptance of the new pricing.',
      },
      refunds: {
        title: 'Refund Policy',
        content:
          'Fees are generally non-refundable. If you cancel your subscription, you will retain access to paid features until the end of your current billing period. No partial refunds are provided for unused portions of a billing period. In exceptional circumstances, refund requests may be considered at our discretion.',
      },
      acceptableUse: {
        title: 'Acceptable Use',
        content:
          "Services must be used only for professional purposes in compliance with Meta's policies. You agree not to use the platform for: spam or unsolicited messages, fraud or deceptive practices, illegal or harmful content, harassment or abuse, misinformation or disinformation, or any activity that violates WhatsApp's terms of service. You are responsible for the content of your messages.",
      },
      whatsapp: {
        title: 'WhatsApp Usage',
        content:
          "Our platform facilitates sending WhatsApp messages through the official WhatsApp Business API. You are responsible for ensuring your messages comply with WhatsApp's policies, including obtaining proper consent from recipients and not sending spam or prohibited content.",
      },
      applicableLaw: {
        title: 'Applicable Law',
        content:
          'These Terms of Service are governed by the laws of Senegal. Any disputes arising from the use of this platform shall be subject to the jurisdiction of Senegalese courts.',
      },
      contact: {
        title: 'Contact Us',
        content:
          'If you have any questions about these Terms of Service, please contact us at contact@dabsi.io.',
      },
    },
  },
  whatchat: {
    modules: {
      inbox: 'Inbox',
      inboxDesc: 'View and respond to conversations',
      metrics: 'Metrics',
      metricsDesc: 'Team performance and response times',
    },
    metrics: {
      title: 'Team Metrics',
      today: 'Today',
      last7days: 'Last 7 days',
      last30days: 'Last 30 days',
      custom: 'Custom',
      pickDateRange: 'Pick date range',
      totalConversations: 'Total conversations',
      agent: 'Agent',
      messagesSent: 'Messages sent',
      conversationsHandled: 'Conversations handled',
      avgFirstResponse: 'Avg. first response',
      avgResolution: 'Avg. resolution time',
      noData: 'No data for this period',
      trendTitle: 'Conversation volume',
      inbound: 'Inbound',
      outbound: 'Outbound',
      viewAgentConversations: 'View conversations for',
    },
    inbox: {
      title: 'Inbox',
      selectPhoneNumber: 'Select phone number',
      noConversations: 'No conversations yet',
      searchContacts: 'Search contacts...',
      selectConversation: 'Select a conversation to start chatting',
      sessionActive: 'Session Active',
      sessionActiveTooltip:
        'Customer messaged within 24h — free-form messaging available',
      sessionExpired: 'Session Expired',
      sessionExpiredTooltip:
        'Only template messages can be sent outside the 24h session window',
      messagePlaceholder: 'Type a message...',
      captionPlaceholder: 'Add a caption...',
      attachFile: 'Attach file',
      send: 'Send',
      templateOnly: 'Session expired — template messages only',
      templateOnlyTitle: 'Session expired',
      templateOnlyDescription:
        'Send a template to reopen the conversation. Free-text replies will be available after the customer responds.',
      chooseTemplate: 'Choose template',
      templateComposerTitle: 'Choose a template',
      templateComposerDescription:
        'Pick a template, review it, and send it from here.',
      templateSendError: 'Failed to send template',
      templateUnsupportedLocation:
        'Location-header templates are not yet supported here.',
      templateUnsupportedButtons:
        'Templates with dynamic coupon buttons are not yet supported here.',
      templateUnsupportedMedia:
        'Templates with image or video headers are not yet supported here.',
      templatePreparingMedia: 'Preparing template media...',
      templateMissingMedia:
        'This template needs example media before it can be sent here.',
      templateUnavailable: 'This template is not available',
      you: 'You:',
      agent: 'Agent',
      noMessages: 'No messages in this conversation',
      loadError: 'Failed to load conversations',
      sendError: 'Failed to send message',
      sendFailed: 'Not sent',
      retry: 'Retry',
      dismiss: 'Dismiss',
      agentMode: 'Agent mode',
      agentModeTooltip: 'Bot is paused — you are handling this conversation',
      releaseToBot: 'Release to bot',
      mediaUnavailable: 'Media unavailable',
      mediaImage: 'Image',
      mediaDocument: 'Document',
      mediaLocation: 'Location',
      mediaContact: 'Contact',
      back: 'Back',
      refresh: 'Refresh',
      pending: 'Pending',
      pendingQueued: 'Pending: queued for delivery',
      pendingProcessing: 'Pending: being processed',
      pendingOutbound: 'Pending: handed off, awaiting delivery updates',
      cannedResponses: 'Quick replies',
      cannedResponsesDesc: 'Saved message snippets you can insert with /',
      addCannedResponse: 'Add quick reply',
      editCannedResponse: 'Edit quick reply',
      deleteCannedResponse: 'Delete quick reply',
      shortcut: 'Shortcut',
      title2: 'Title',
      content: 'Content',
      noCannedResponses: 'No quick replies yet',
      noCannedResponseMatches: 'No quick replies match your search',
      cannedResponseSaved: 'Quick reply saved',
      cannedResponseDeleted: 'Quick reply deleted',
      confirmDeleteCannedResponse:
        'Are you sure you want to delete this quick reply?',
      statusOpen: 'Open',
      statusPending: 'Pending',
      statusResolved: 'Resolved',
      statusArchived: 'Archived',
      statusAll: 'All',
      changeStatus: 'Change status',
      assignedTo: 'Assigned to',
      unassigned: 'Unassigned',
      assignAgent: 'Assign agent',
      myConversations: 'Mine',
      allConversations: 'All',
      unassignedConversations: 'Unassigned',
      contactInfo: 'Contact info',
      tags: 'Tags',
      about: 'About',
      addAbout: 'Add description',
      groups: 'Groups',
      stats: 'Statistics',
      totalMessages: 'Total messages',
      firstMessage: 'First message',
      lastMessage: 'Last message',
      noContactInfo: 'No contact info available',
      notesSaved: 'Notes saved',
      tagsSaved: 'Tags saved',
      addTags: 'Add tags...',
      notesPlaceholder: 'Add notes about this contact...',
      conversationNotes: 'Notes',
      addNote: 'Add a note...',
      noteSaved: 'Note saved',
      noNotes: 'No notes yet',
      deleteNote: 'Delete note',
      deleteNoteConfirm:
        'Are you sure you want to delete this note? This cannot be undone.',
      filteredByAgent: 'Filtered: {email}',
      filteredByDate: 'Date: {from} – {to}',
      clearFilter: 'Clear filter',
      viewing: 'viewing',
      filterStatus: 'Status',
      filterAssignment: 'Assignment',
      filterUnreadOnly: 'Unread only',
      filterDateRange: 'Date range',
      filterTags: 'Tags',
      filterTagsPlaceholder: 'Any tags',
      noTagsFound: 'No matching tags',
      moreFilters: 'More filters',
      selected: 'selected',
      selectAll: 'Select all',
      conversations: 'conversations',
      conversation: 'conversation',
      resolve: 'Resolve',
      archive: 'Archive',
      clearSelection: 'Clear',
      bulkActionSuccess: 'Updated successfully',
      assignTo: 'Assign to...',
      newMessage: 'New message',
      notificationPermission:
        'Enable notifications to get alerted when new messages arrive',
      enableNotifications: 'Enable notifications',
    },
  },
  pricing: {
    // Landing page pricing section
    title: 'Simple, Per-App Pricing',
    subtitle:
      'Pick the apps you need. Each has its own plan. More apps, bigger discount.',
    seeFullDetails: 'See full pricing details',
    perMonth: '/mo',
    startingAt: 'From',
    contactUs: 'Contact Us',
    getStarted: 'Get Started',
    custom: 'Custom',
    customDescription: 'Need more? Let\u2019s talk.',

    // Tier names
    starter: 'Starter',
    pro: 'Pro',

    // Section headings
    coreApps: 'Core Apps',
    addOns: 'Add-ons',
    bundleDiscounts: 'Bundle Discounts',

    // App names (short, for tables)
    apps: {
      clientell: 'Clientell',
      whatchat: 'WhatChat',
      rakine: 'Rakine',
      imminote: 'Imminote',
    },
    // App taglines (for pricing page detail)
    appTaglines: {
      clientell: 'Bulk WhatsApp messaging',
      whatchat: 'Shared inbox for live chat',
      rakine: 'Delivery management',
      imminote: 'Automated notifications via API',
    },

    // Per-app feature lists (Starter)
    appFeatures: {
      clientellStarter: [
        'Up to 5,000 contacts',
        '20,000 messages/mo',
        '1 phone number',
        'Analytics',
      ],
      clientellPro: [
        'Up to 25,000 contacts',
        '100,000 messages/mo',
        '5 phone numbers',
        'Message scheduling',
        'Full API access',
        'Analytics',
      ],
      whatchatStarter: ['2 agent seats', 'Shared inbox', 'Real-time updates'],
      whatchatPro: [
        '10 agent seats',
        'Shared inbox',
        'Real-time updates',
        'AI conversation summaries',
      ],
      rakineStarter: [
        'Up to 100 drivers',
        'Real-time tracking',
        'Delivery dispatch',
      ],
      rakinePro: [
        'Up to 500 drivers',
        'Real-time tracking',
        'Delivery dispatch',
        'AI search & insights',
      ],
      imminoteStarter: ['2,000 API messages/mo', 'REST API + SDKs'],
      imminotePro: ['10,000 API messages/mo', 'REST API + SDKs', 'Webhooks'],
    },

    // Add-on labels
    addOnLabels: {
      botmaker: 'Botmaker',
      extra_seats: 'Extra Team Seats',
      extra_numbers: 'Extra Phone Numbers',
      priority_support: 'Priority Support',
      data_export: 'Data Export',
    },
    addOnDescriptions: {
      botmaker:
        'No-code chatbot builder \u2014 works with Clientell, WhatChat & Rakine',
      extra_seats: 'Additional team member seats across all your apps',
      extra_numbers: 'Additional WhatsApp phone numbers',
      priority_support: 'Priority email & chat support for all apps',
      data_export: 'Export your data across all subscribed apps',
    },

    // Bundle discount text
    bundleNote: 'Subscribe to multiple apps and save automatically.',
    bundleTiers: [
      '2 apps \u2014 10% off',
      '3 apps \u2014 15% off',
      '4 apps \u2014 20% off',
    ],

    // Checkout page
    checkoutTitle: 'Build Your Plan',
    checkoutSubtitle: 'Select the apps and add-ons your team needs.',
    checkoutChooseApps: 'Choose Your Apps',
    checkoutSelectTier: 'Select a tier',
    checkoutAddOns: 'Add-ons (optional)',
    checkoutSummary: 'Summary',
    checkoutSubtotal: 'Subtotal',
    checkoutBundleDiscount: 'Bundle discount',
    checkoutTotal: 'Total',
    checkoutSubscribe: 'Subscribe',
    checkoutConfirmChanges: 'Confirm changes',
    checkoutYouSave: 'You save {percent}% with the bundle discount!',
    checkoutEmpty: 'Select at least one app to get started.',
    checkoutSuccessTitle: 'Subscription submitted',
    checkoutSuccessMessage:
      'Your subscription has been submitted successfully. We will get back to you shortly.',
    checkoutErrorMessage: 'Something went wrong. Please try again.',
    checkoutPaymentPendingTitle: 'Subscription saved, payment pending',
    checkoutPaymentPendingMessage:
      'Your trial was activated but we could not open the payment page. Try paying again from your profile, or contact support if the problem persists.',
    checkoutConfirmTitle: 'Confirm Subscription',
    checkoutConfirmTitleUpdate: 'Update Subscription',
    checkoutConfirmMessage:
      'You are about to subscribe to the following plan. You can change or cancel anytime.',
    checkoutConfirmMessageUpdate:
      'Your subscription will be updated to the following plan. Changes take effect immediately.',
    checkoutConfirmBack: 'Go Back',
    checkoutConfirmProceed: 'Confirm & Subscribe',
    checkoutConfirmProceedUpdate: 'Confirm & Update',
    checkoutAdminRequiredTitle: 'Admin access required',
    checkoutAdminRequiredMessage:
      'Only an admin can manage subscriptions. Contact your organization admin.',
    noActiveAppsTitle: 'No active apps',
    noActiveAppsMessage:
      'Your organization has no active subscriptions. Subscribe to get started.',
    browsePlans: 'Browse Plans',
    checkoutMobileSummary: 'View Summary',
    pricingLoadError:
      "Couldn't load pricing. Check your connection and try again.",
    retry: 'Retry',
    nextRenewalOn: 'Next renewal on {date}',
    dueToday: 'Due today',
    cancelling: 'Cancelling',
    cancelsAtRenewal: 'Ends at next renewal',
    undoCancel: 'Restore',
    undoCancelError:
      "Couldn't undo the cancellation. If the subscription already ended, re-subscribe from the checkout instead.",
    alreadyPaidThisCycle: 'Already paid this cycle',
    nothingToCommitHint: 'Your subscription is up to date — nothing to save.',
    checkoutNoChargeTitle: 'Nothing to pay',
    checkoutNoChargeMessage:
      'Your subscription is up to date. No charge is needed right now.',

    // Subscription management (profile page)
    yourApps: 'Your Apps',
    noActiveSubscriptions: 'No active subscriptions. Subscribe to get started.',
    managePlan: 'Manage Plan',
    trialEnds: 'Trial ends',
    nextBilling: 'Next billing',
    cancelApp: 'Cancel',
    cancelAppConfirm:
      'Are you sure you want to cancel {app}? Access continues until the end of your billing period.',
    cancelAppSuccess:
      '{app} subscription will be cancelled at the end of the billing period.',
    cancelAppError: 'Failed to cancel subscription. Please try again.',
    paymentRequired: 'Payment required',
    paymentPausedMessage: 'Access is paused until payment completes.',
    payNow: 'Pay now',
    payNowError: "Couldn't start payment. Please try again.",
    monthlyTotal: 'Monthly Total',
    nextRenewalTotal: 'Next renewal',
    activeAddOns: 'Active Add-ons',
    bundleDiscount: 'Bundle discount',
    quantity: 'Qty',

    // Mobile bar
    mobileBarOneApp: '1 app',
    mobileBarMultipleApps: '{count} apps',

    // FAQ (new, app-based)
    faqTitle: 'FAQ',
    faqs: [
      {
        question: 'How does the free trial work?',
        answer:
          'Each app includes a 14-day free trial on the Starter tier, no credit card required. The trial is one-time per app — if you cancel Clientell and later resubscribe, you skip the trial and go straight to a paid subscription.',
      },
      {
        question: 'Can I subscribe to just one app?',
        answer:
          'Absolutely. Pick only the apps you need. You can add more anytime.',
      },
      {
        question: 'How does billing work?',
        answer:
          'Your whole organization is on one billing cycle. When your first payment lands, we set your next renewal date 30 days out. Every app you add after that renews on the same date — one invoice, one cycle, no scattered dates to track.',
      },
      {
        question: 'What happens if I add an app or upgrade mid-cycle?',
        answer:
          'You only pay for the days remaining until your next renewal, prorated from the new app or tier\u2019s price. Your renewal date stays the same, so next month everything renews together. Upgrading (e.g. Starter to Pro) charges the prorated difference for the days left in your cycle.',
      },
      {
        question: 'Can I cancel an app or my whole subscription?',
        answer:
          'Yes, anytime. You keep access until the end of your current cycle — there\u2019s no mid-cycle refund — and the cancelled app simply doesn\u2019t renew. You can resubscribe later whenever you want.',
      },
      {
        question: 'How do bundle discounts work?',
        answer:
          'Discounts are applied automatically when you subscribe to 2 or more apps. The more apps you use, the bigger the discount.',
      },
      {
        question: 'What payment methods do you accept?',
        answer:
          'We accept card payments, Orange Money, and Wave Money. Custom tier customers can discuss additional payment options.',
      },
      {
        question: 'What is the Custom tier?',
        answer:
          'The Custom tier is for businesses that need higher limits, dedicated support, or custom integrations. Contact our team and we\u2019ll build a plan that fits.',
      },
      {
        question: 'How does Botmaker work as an add-on?',
        answer:
          'Botmaker is a no-code chatbot builder that can be added to any app. One subscription covers all your connected apps.',
      },
      {
        question: 'Do you offer discounts for non-profits?',
        answer:
          'Yes. Contact our sales team for special pricing for registered non-profit organizations.',
      },
    ],
  },
  admin: {
    pageTitle: 'Pricing Administration',
    pageSubtitle: 'Manage plans, add-ons, and organization subscriptions.',
    plansTab: 'Plans',
    addOnsTab: 'Add-ons',
    orgsTab: 'Organizations',
    app: 'App',
    starterPrice: 'Starter',
    proPrice: 'Pro',
    features: 'Features Key',
    bundleDiscountsTitle: 'Bundle Discounts',
    apps: 'apps',
    discount: 'discount',
    addOnName: 'Add-on',
    price: 'Price',
    stackable: 'Stackable',
    unit: 'Unit',
    quoteBased: 'Quote-based',
    yes: 'Yes',
    no: 'No',
    orgName: 'Organization',
    subscribedApps: 'Subscribed Apps',
    activeAddOns: 'Add-ons',
    status: 'Status',
    manage: 'Manage',
    none: 'None',
    noOrgs: 'No organizations found.',
    manageOrg: 'Manage Subscription',
    appSubscriptions: 'App Subscriptions',
    addOnsTitle: 'Add-ons',
    off: 'Off',
    on: 'On',
    subtotal: 'Subtotal',
    bundleDiscount: 'Bundle discount',
    monthlyTotal: 'Monthly total',
    cancel: 'Cancel',
    saveChanges: 'Save Changes',
    adminPricing: 'Admin: Pricing',
    editPrices: 'Edit Prices',
    editPricesFor: 'Edit prices for',
    confirmPriceChange: 'Are you sure you want to update these prices?',
    priceUpdateSuccess: 'Prices updated successfully.',
    priceUpdateError: 'Failed to update prices.',
    currentPrice: 'Current',
    newPrice: 'New',
    noChanges: 'No changes to save.',
    perMonth: '/mo',
    editFeatures: 'Edit Features',
    editFeaturesFor: 'Edit features for',
    featureKey: 'Feature Key',
    featureValue: 'Value',
    addFeature: 'Add Feature',
    selectFeatureKey: 'Select a feature',
    removeFeature: 'Remove',
    featureUpdateSuccess: 'Features updated successfully.',
    featureUpdateError: 'Failed to update features.',
    confirmFeatureChange: 'Are you sure you want to update these features?',
    noFeatureChanges: 'No feature changes to save.',
    currentValue: 'Current',
    newValue: 'New',
    removed: 'Removed',
    added: 'Added',
  },
};
